// Custom hook para bloquear navegación interna en React Router v6
import React, { useState, useEffect, useRef } from 'react';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Dialog as MuiDialog, DialogTitle as MuiDialogTitle, DialogContent as MuiDialogContent, DialogActions as MuiDialogActions } from '@mui/material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  ArrowBack, 
  Add, 
  Delete, 
  Business, 
  Person, 
  Home, 
  LocalShipping, 
  Build,
  Receipt,
  ExpandMore,
  Edit,
  Visibility,
  Download,
  Email,
  CheckCircle,
  Cancel,
  Assignment,
  HandymanOutlined,
  AccountBox
} from '@mui/icons-material';
import { 
  Budget,
  BudgetVersion,
  BudgetVersionCreate, 
  EnvironmentCreate, 
  EnvironmentMaterialCreate, 
  FreightCreate,
  EnvironmentInstallationCreate,
  Product
} from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';
import { apiService } from '../../services/apiService';

// Constante para el ambiente de instalaciones generales (oculto en materiales)
const GENERAL_INSTALLATIONS_ENV_NAME = '__INSTALACIONES_GENERALES__';
const GENERAL_INSTALLATIONS_INDEX = -1;

interface EnvironmentFormData {
  id?: number;
  nombre: string;
  descripcion: string;
  orden: number;
  descuento_materiales?: number;  // Descuento en % para materiales de este ambiente
  materials: MaterialFormData[];
  installations: InstallationFormData[];
}

interface MaterialFormData {
  id?: number;
  // Producto existente o nuevo
  product_id?: number;
  product?: Product;
  es_in_situ: boolean;
  
  // Datos del material
  descripcion: string;
  codigo: string;
  unidades: number;
  precio_unitario: number;
  currency: 'USD' | 'ARS';
  
  // Datos de cálculo
  metros_cuadrados?: number;
  cantidad_calculada?: number;
  cantidad_manual?: number;
  usar_cantidad_manual?: boolean;
  
  // Opciones de producto
  con_desperdicio?: boolean;
  con_estructura?: boolean;
  
  // Observaciones
  observaciones?: string;
}

interface InstallationFormData {
  id?: number;
  installation_service_id?: number;
  installation_service?: any; // InstallationService type, import if available
  tipo_instalacion?: string;
  cantidad_manual?: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom
  currency: 'USD' | 'ARS';
}

interface FreightFormData {
  id?: number;
  tipo_envio: string;
  descripcion_detalle?: string;
  monto: number;
  tipo: 'fijo' | 'porcentaje';
  currency: 'USD' | 'ARS';
}

interface BudgetDetailData {
  // Datos del presupuesto
  id: number;
  proyecto: string;
  cliente: string;
  responsable: string;
  email_cliente: string;
  tel_cliente: string;
  status: string;
  code: string;
  
  // Datos de la versión actual
  currentVersion: BudgetVersion | null;
  versions: BudgetVersion[];
  
  // Datos de ambientes, fletes, etc. (de la versión actual)
  environments: EnvironmentFormData[];
  freights: FreightFormData[];
  descuento_materiales: number;
  descuento_instalacion: number;
  descuento_fletes: number;
}

const BudgetDetailNew: React.FC = () => {
  // Estados para navegación y dirty state
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [nextLocation, setNextLocation] = useState<string | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Estados principales de datos y carga
  const [formData, setFormData] = useState<BudgetDetailData>({
    id: 0,
    proyecto: '',
    cliente: '',
    responsable: '',
    email_cliente: '',
    tel_cliente: '',
    status: 'draft',
    code: '',
    currentVersion: null,
    versions: [],
    environments: [],
    freights: [],
    descuento_materiales: 0,

    descuento_instalacion: 0,
    descuento_fletes: 0
  });

  // Solo lectura total si la versión está vendida
  const isReadOnly = formData.currentVersion?.estado === 'sold';
  // Solo los campos quedan en solo lectura si está cerrada
  const isFieldsReadOnly = formData.currentVersion?.estado === 'closed';

  // Botones de nueva versión solo visibles si el presupuesto NO está vendido
  const showVersionActions = formData.status !== 'sold';
  // Admin puede cambiar estado aunque el presupuesto esté vendido
  const canChangeStatus = showVersionActions || user?.role === 'admin';

  // Handler genérico para cambios de descuento
  const handleChangeDescuento = (field: 'descuento_materiales' | 'descuento_instalacion' | 'descuento_fletes') => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    if (value > 100) value = 100;
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };
  const [loading, setLoading] = useState(true);
  // formData and loading are declared later, only declare once
  useEffect(() => {
    // No marcar dirty al cargar datos, solo por acciones del usuario
  }, []);

  // Resetear dirty cuando se guarda exitosamente
  const handleAfterSave = () => setIsDirty(false);

  // Bloquear navegación por refresh/cerrar pestaña
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Bloquear navegación interna (React Router v6 workaround)
  useEffect(() => {
    if (!isDirty) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return;
    }
    if (!unblockRef.current) {
      // @ts-ignore
      unblockRef.current = navigate((tx: any) => {
        if (isDirty && tx.location.pathname !== location.pathname) {
          setShowLeaveDialog(true);
          setNextLocation(tx.location.pathname);
          return false;
        }
        return true;
      });
    }
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isDirty, navigate, location.pathname]);

  // Handler para guardar y salir
  const handleSaveAndLeave = async () => {
    if (nextLocation) {
      await updateAllBudgetData();
      setIsDirty(false);
      setShowLeaveDialog(false);
      navigate(nextLocation);
    }
  };

  // Handler para salir sin guardar
  const handleLeaveWithoutSaving = () => {
    setIsDirty(false);
    setShowLeaveDialog(false);
    if (nextLocation) navigate(nextLocation);
  };
  // ...existing code...
  
  // Estados para productos
  const [products, setProducts] = useState<Product[]>([]);
  
  // Estados principales
  const [budget, setBudget] = useState<Budget | null>(null);
  const [versions, setVersions] = useState<BudgetVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<BudgetVersion | null>(null);
  // (removed duplicate formData declaration)


  // Diálogo de confirmación de salida si hay cambios no guardados
  const leaveDialog = (
    <MuiDialog open={showLeaveDialog} onClose={() => setShowLeaveDialog(false)}>
      <MuiDialogTitle>Hay cambios sin guardar</MuiDialogTitle>
      <MuiDialogContent>
        <Typography>¿Deseas guardar los cambios antes de salir?</Typography>
      </MuiDialogContent>
      <MuiDialogActions>
        <Button onClick={handleLeaveWithoutSaving} color="error">Salir sin guardar</Button>
        <Button onClick={handleSaveAndLeave} color="primary" variant="contained">Guardar y salir</Button>
      </MuiDialogActions>
    </MuiDialog>
  );
  
  // Estados de UI
  // (removed duplicate loading declaration)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCondiciones, setShowCondiciones] = useState(false);
  
  // Estados para diálogos
  const [environmentDialog, setEnvironmentDialog] = useState(false);
  const [materialDialog, setMaterialDialog] = useState(false);
  const [freightDialog, setFreightDialog] = useState(false);
  const [installationDialog, setInstallationDialog] = useState(false);
  const [newVersionDialog, setNewVersionDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  
  // Estados para formularios de diálogos
  const [selectedEnvironmentIndex, setSelectedEnvironmentIndex] = useState<number | null>(null);
  const [newEnvironment, setNewEnvironment] = useState<EnvironmentFormData>({
    nombre: '',
    descripcion: '',
    orden: 1,
    materials: [],
    installations: []
  });
  const [newMaterial, setNewMaterial] = useState<MaterialFormData>({
    descripcion: '',
    codigo: '',
    unidades: 0,
    precio_unitario: 0,
    currency: 'USD',
    es_in_situ: false,
    metros_cuadrados: 1,
    usar_cantidad_manual: false,
    con_desperdicio: true,
    con_estructura: false,
    observaciones: ''
  });
  const [newFreight, setNewFreight] = useState<FreightFormData>({
    tipo_envio: '',
    monto: 0,
    tipo: 'fijo',
    currency: 'ARS'
  });
  const [installationServices, setInstallationServices] = useState<any[]>([]);
  const [loadingInstallationServices, setLoadingInstallationServices] = useState(false);
  const [newInstallation, setNewInstallation] = useState<InstallationFormData>({
    cantidad_manual: 1,
    currency: 'ARS'
  });
  const [installationMode, setInstallationMode] = useState<'catalog' | 'custom'>('catalog');
  const [alertStock, setAlertStock] = useState<string | null>(null);
  const [showAlertStock, setShowAlertStock] = useState(false);
  
  // Estado para edición de nombres de ambientes
  const [editingEnvironmentId, setEditingEnvironmentId] = useState<number | null>(null);
  const [editingEnvironmentName, setEditingEnvironmentName] = useState<string>('');
  const [savingEnvironmentName, setSavingEnvironmentName] = useState(false);
  
  // Estado para el input de descuento global (solo para escritura)
  const [globalDiscountInput, setGlobalDiscountInput] = useState<string>('');
  
  // Calcular si todos los ambientes tienen el mismo descuento
  const getCommonDiscount = (): number | null => {
    const envs = formData.environments.filter(e => e.nombre !== GENERAL_INSTALLATIONS_ENV_NAME);
    if (envs.length === 0) return null;
    const firstDiscount = envs[0].descuento_materiales || 0;
    const allSame = envs.every(env => (env.descuento_materiales || 0) === firstDiscount);
    return allSame ? firstDiscount : null;
  };
  
  const commonDiscount = getCommonDiscount();
  
  // Actualizar el input cuando los descuentos individuales cambien y sean uniformes
  useEffect(() => {
    if (commonDiscount !== null) {
      setGlobalDiscountInput(Math.round(commonDiscount).toString());
    } else {
      setGlobalDiscountInput('');
    }
  }, [commonDiscount]);
  
  // Función para aplicar descuento global a todos los ambientes
  const applyGlobalDiscount = () => {
    const value = parseFloat(globalDiscountInput);
    if (isNaN(value) || value < 0 || value > 100) return;
    
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.map(env => ({
        ...env,
        descuento_materiales: value
      }))
    }));
    setIsDirty(true);
  };
  
  // Cargar installation services al inicio
  useEffect(() => {
    loadInstallationServices();
  }, []);

  const loadInstallationServices = async () => {
    try {
      setLoadingInstallationServices(true);
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
  const response = await fetch(`/api/installation-services?is_active=true&limit=100`, {
        method: 'GET',
        headers
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const services = await response.json();
      setInstallationServices(Array.isArray(services) ? services : []);
    } catch (error) {
      setError('Error al cargar servicios de instalación');
    } finally {
      setLoadingInstallationServices(false);
    }
  };

  // Handler para seleccionar servicio de instalación
  const handleInstallationServiceSelect = (service: any | null) => {
    if (!service) {
      setNewInstallation(prev => ({
        ...prev,
        installation_service_id: undefined,
        installation_service: undefined,
        tipo_instalacion: undefined,
        trabajo: undefined
      }));
      return;
    }

    // Calcular área del ambiente si hay uno seleccionado y la unidad es m²
    let cantidadInicial = 1;
    if (selectedEnvironmentIndex !== null && formData.environments[selectedEnvironmentIndex]) {
      const unidad = service.unidad?.toLowerCase() || '';
      if (unidad === 'm²' || unidad === 'm2' || unidad === 'm2') {
        const areaTotal = formData.environments[selectedEnvironmentIndex].materials.reduce(
          (total, mat) => total + (mat.metros_cuadrados || 0), 0
        );
        if (areaTotal > 0) {
          cantidadInicial = Math.round(areaTotal * 100) / 100; // Redondear a 2 decimales
        }
      }
    }

    setNewInstallation(prev => ({
      ...prev,
      installation_service_id: service.id,
      installation_service: service,
      trabajo: service.trabajo || '',
      descripcion_trabajo: service.descripcion || '', // Cargar la descripcion del catálogo
      cantidad_manual: cantidadInicial
    }));
  };

  // Calcular precio según tipo de instalación
  const calculateInstallationPrice = (): number => {
    // Si el usuario editó el precio (mano_obra_m2), usarlo directamente
    if (newInstallation.mano_obra_m2) {
      return newInstallation.mano_obra_m2;
    }
    
    // Si no hay precio editado, calcular desde el catálogo
    if (!newInstallation.installation_service || !newInstallation.tipo_instalacion) {
      return 0;
    }
    const service = newInstallation.installation_service;
    if (newInstallation.tipo_instalacion === 'standard') {
      return service.precio_standard || 0;
    } else if (newInstallation.tipo_instalacion === 'compleja') {
      return service.precio_complejo || 0;
    }
    return 0;
  };

  // Formatear moneda específica para installation services
  const formatInstallationCurrency = (amount: number, currency: string) => {
    if (currency === 'USD') {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };
  const [newVersionData, setNewVersionData] = useState<Partial<BudgetVersionCreate>>({
    titulo: '',
    vendedor: '',
    email_vendedor: '',
    tel_vendedor: '',
    condiciones_contratacion: '',
    numero_version: '',
    nombre: '',
    condiciones: ''
  });

  // Estados para manejo de versiones
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Cargar datos al inicio
  useEffect(() => {
    if (id) {
      loadBudgetData();
      loadProducts();
    }
  }, [id]);

  // Efecto para manejar la alerta de stock
  useEffect(() => {
    if (alertStock) {
      setShowAlertStock(true);
      const timer = setTimeout(() => {
        setShowAlertStock(false);
        setAlertStock(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alertStock]);

  const loadProducts = async () => {
    try {
      const productsData = await apiService.products.getAll({ active_only: true, limit: 1000 });
      setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadBudgetData = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const [budgetData, versionsData] = await Promise.all([
        BudgetApiService.getBudget(parseInt(id)),
        BudgetApiService.getBudgetVersions(parseInt(id))
      ]);
      
      setBudget(budgetData);
      
      // Seleccionar siempre la última versión (mayor version_number)
      // El backend devuelve las versiones ordenadas por version_number DESC
      const currentVersionData: BudgetVersion | null = versionsData.length > 0 ? versionsData[0] : null;
      
      // Marcar la versión actual en el array
      const versionsWithCurrent = versionsData.map(v => ({
        ...v,
        is_current: currentVersionData ? v.id === currentVersionData.id : false
      }));
      setVersions(versionsWithCurrent);

      if (currentVersionData) {
        try {
          const versionWithDetails = await BudgetApiService.getBudgetVersion(parseInt(id), currentVersionData.id);
          setCurrentVersion(versionWithDetails);
          
          // Enriquecer materiales con información completa del producto
          const enrichedEnvironments = await Promise.all(
            (versionWithDetails.environments || []).map(async (env: any) => ({
              ...env,
              materials: await Promise.all(
                (env.materials || []).map(async (mat: any) => {
                  // Si el material tiene product_id, cargar información completa del producto
                  if (mat.product_id && !mat.es_in_situ) {
                    try {
                      const productInfo = await apiService.products.getById(mat.product_id);
                      return {
                        ...mat,
                        product: productInfo
                      };
                    } catch (err) {
                      console.warn(`Error loading product ${mat.product_id}:`, err);
                      return mat;
                    }
                  }
                  return mat;
                })
              )
            }))
          );
          
          // Mapear los datos a la estructura del formulario
          setFormData({
            id: budgetData.id,
            proyecto: budgetData.proyecto,
            cliente: budgetData.cliente,
            responsable: budgetData.responsable || '',
            email_cliente: budgetData.email_cliente || '',
            tel_cliente: budgetData.tel_cliente || '',
            status: budgetData.status,
            code: budgetData.code,
            currentVersion: versionWithDetails,
            versions: versionsData,
            environments: mapEnvironmentsToFormData(enrichedEnvironments),
            freights: mapFreightsToFormData(versionWithDetails.freights || []),
            descuento_materiales: (versionWithDetails as any).descuento_materiales ?? 0,
            descuento_instalacion: (versionWithDetails as any).descuento_instalacion ?? 0,
            descuento_fletes: (versionWithDetails as any).descuento_fletes ?? 0
          });
        } catch (versionError) {
          console.error('Error loading version details:', versionError);
        }
      }
    } catch (err: any) {
      console.error('Error loading budget:', err);
      setError(err.response?.data?.detail || 'Error al cargar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  // Función para mapear datos de ambientes a estructura del formulario
  const mapEnvironmentsToFormData = (environments: any[]): EnvironmentFormData[] => {
    return environments.map(env => ({
      id: env.id,
      nombre: env.nombre,
      descripcion: env.descripcion || '',
      orden: env.orden,
      descuento_materiales: env.descuento_materiales || 0,
      materials: (env.materials || []).map((mat: any) => ({
        id: mat.id,
        descripcion: mat.descripcion,
        codigo: mat.codigo,
        unidades: mat.unidades,
        precio_unitario: mat.precio_unitario,
        currency: mat.currency || 'USD',
        es_in_situ: mat.es_in_situ || false,
        product_id: mat.product_id,
        product: mat.product, // Incluir información del producto
        observaciones: mat.observaciones || '',
        metros_cuadrados: mat.metros2 || 0,
        con_desperdicio: mat.con_desperdicio || false, // Agregar flag de desperdicio
        con_estructura: mat.con_estructura || false // Agregar flag de estructura
      })),
      installations: (env.installations || []).map((inst: any) => ({
        id: inst.id,
        trabajo: inst.trabajo || inst.descripcion, // Compatibilidad con datos antiguos
        descripcion_trabajo: inst.descripcion_trabajo,
        mano_obra_m2: inst.mano_obra_m2,
        currency: inst.currency || 'ARS',
        installation_service_id: inst.installation_service_id,
        installation_service: inst.installation_service,
        tipo_instalacion: inst.tipo_instalacion,
        cantidad_manual: inst.cantidad_manual,
        unidad: inst.unidad
      }))
    }));
  };

  // Función para mapear datos de fletes a estructura del formulario
  const mapFreightsToFormData = (freights: any[]): FreightFormData[] => {
    return freights.map(freight => ({
      id: freight.id,
      tipo_envio: freight.tipo_envio || freight.descripcion || '', // Compatibilidad durante migración
      descripcion_detalle: freight.descripcion_detalle,
      monto: freight.monto,
      tipo: freight.tipo || 'fijo',
      currency: freight.currency || 'ARS'
    }));
  };

  // Funciones de cálculo (copiadas de BudgetFormNew)
  const calculateMaterialQuantity = (product: Product, metros_cuadrados: number, con_desperdicio: boolean = false): number => {
    if (!product.largo || !product.ancho) return 1;
    
    // Usar ancho_util si existe (para considerar solapamiento), sino usar ancho normal
    const ancho_efectivo = product.ancho_util ?? product.ancho;
    const area_por_unidad = product.largo * ancho_efectivo;
    
    if (area_por_unidad === 0) {
      return Math.ceil(metros_cuadrados);
    }
    
    let cantidad = Math.ceil(metros_cuadrados / area_por_unidad);
    
    if (con_desperdicio) {
      cantidad = Math.ceil(cantidad * 1.1);
    }
    
    return cantidad;
  };

  const calculateMetrosCuadradosFromQuantity = (product: Product, cantidad: number): number => {
    if (!product.largo || !product.ancho || cantidad <= 0) return 0;
    
    // Usar ancho_util si existe (para considerar solapamiento), sino usar ancho normal
    const ancho_efectivo = product.ancho_util ?? product.ancho;
    const area_por_unidad = product.largo * ancho_efectivo;
    return cantidad * area_por_unidad;
  };

  // Funciones de manejo de productos (copiadas de BudgetFormNew)
  const handleProductSelect = (product: Product | null) => {
    if (!product) {
      setNewMaterial(prev => ({
        ...prev,
        product_id: undefined,
        product: undefined,
        descripcion: '',
        codigo: '',
        precio_unitario: 0,
        cantidad_calculada: 0
      }));
      return;
    }

    const cantidadCalculada = calculateMaterialQuantity(
      product, 
      newMaterial.metros_cuadrados || 1, 
      newMaterial.con_desperdicio || false
    );
    
    setNewMaterial(prev => ({
      ...prev,
      product_id: product.id,
      product: product,
      descripcion: product.codigo,
      codigo: product.codigo,
      precio_unitario: product.precio_unitario || 0,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  const handleMetrosCuadradosChange = (metros: number) => {
    if (!newMaterial.product) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      metros, 
      newMaterial.con_desperdicio || false
    );
    
    setNewMaterial(prev => ({
      ...prev,
      metros_cuadrados: metros,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  const handleCantidadManualChange = (cantidad: number) => {
    if (!newMaterial.product) return;
    
    let cantidadBase = cantidad;
    
    if (newMaterial.con_desperdicio) {
      cantidadBase = Math.ceil(cantidad / 1.1);
    }
    
    const metrosCalculados = calculateMetrosCuadradosFromQuantity(newMaterial.product, cantidadBase);
    
    setNewMaterial(prev => ({
      ...prev,
      cantidad_manual: cantidad,
      unidades: cantidad,
      metros_cuadrados: Math.round(metrosCalculados)
    }));
  };

  const handleDesperdicioChange = (con_desperdicio: boolean) => {
    if (!newMaterial.product || !newMaterial.metros_cuadrados) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      newMaterial.metros_cuadrados, 
      con_desperdicio
    );
    
    setNewMaterial(prev => ({
      ...prev,
      con_desperdicio,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  // Funciones de manejo de ambientes
  const handleAddEnvironment = () => {
    if (!newEnvironment.nombre.trim()) return;

    setFormData(prev => ({
      ...prev,
      environments: [...prev.environments, { 
        ...newEnvironment, 
        orden: prev.environments.length + 1,
        materials: []
      }]
    }));

    setNewEnvironment({
      nombre: '',
      descripcion: '',
      orden: 1,
      materials: [],
      installations: []
    });
    setEnvironmentDialog(false);
  };

  const handleDeleteEnvironment = async (index: number) => {
    try {
      setSaving(true);
      const environment = formData.environments[index];
      
      console.log(`🗑️ Intentando eliminar ambiente:`, {
        index,
        environment,
        hasId: !!environment.id,
        id: environment.id,
        materialsCount: environment.materials?.length || 0,
        hasInstallations: !!(environment.installations && environment.installations.length > 0)
      });
      
      if (environment.id && environment.id > 0) {
        // Primero eliminar todos los materiales del ambiente
        if (environment.materials && environment.materials.length > 0) {
          console.log(`🔥 Eliminando ${environment.materials.length} materiales del ambiente`);
          for (const material of environment.materials) {
            if (material.id && material.id > 0) {
              await BudgetApiService.deleteEnvironmentMaterial(material.id);
              console.log(`✅ Material "${material.descripcion}" eliminado del backend`);
            }
          }
        }
        
        // Eliminar las instalaciones si existen
        if (environment.installations && environment.installations.length > 0) {
          for (const installation of environment.installations) {
            if (installation?.id && installation.id > 0) {
              console.log(`🔥 Eliminando instalación del ambiente`);
              await BudgetApiService.deleteEnvironmentInstallation(installation.id);
              console.log(`✅ Instalación eliminada del backend`);
            }
          }
        }
        
        // Finalmente eliminar el ambiente
        console.log(`🔥 Eliminando ambiente del backend con ID: ${environment.id}`);
        await BudgetApiService.deleteEnvironment(environment.id);
        console.log(`✅ Ambiente "${environment.nombre}" eliminado del backend`);
      } else {
        console.log(`⚠️ Ambiente sin ID válido, solo eliminando del estado local:`, environment);
      }

      // Eliminar del estado local
      setFormData(prev => ({
        ...prev,
        environments: prev.environments.filter((_, i) => i !== index)
      }));
      
      console.log(`✅ Ambiente eliminado del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando ambiente:', error);
      setError('Error al eliminar el ambiente');
    } finally {
      setSaving(false);
    }
  };

  // Funciones de edición de nombre de ambiente
  const handleStartEditEnvironmentName = (envId: number, currentName: string) => {
    setEditingEnvironmentId(envId);
    setEditingEnvironmentName(currentName);
  };

  const handleCancelEditEnvironmentName = () => {
    setEditingEnvironmentId(null);
    setEditingEnvironmentName('');
  };

  const handleSaveEnvironmentName = async (envId: number) => {
    // Validar nombre no vacío
    const trimmedName = editingEnvironmentName.trim();
    if (!trimmedName) {
      setError('El nombre del ambiente no puede estar vacío');
      return;
    }

    try {
      setSavingEnvironmentName(true);
      
      // Llamada al API para actualizar el nombre
      await apiService.put(`/budgets/environments/${envId}`, {
        nombre: trimmedName
      });

      // Actualizar estado local
      setFormData(prev => ({
        ...prev,
        environments: prev.environments.map(env =>
          env.id === envId ? { ...env, nombre: trimmedName } : env
        )
      }));

      // Limpiar estado de edición
      setEditingEnvironmentId(null);
      setEditingEnvironmentName('');
      
      setSuccess('Nombre del ambiente actualizado correctamente');
    } catch (error: any) {
      console.error('Error actualizando nombre del ambiente:', error);
      setError(error.response?.data?.detail || 'Error al actualizar el nombre del ambiente');
    } finally {
      setSavingEnvironmentName(false);
    }
  };

  // Funciones de manejo de materiales
  const handleAddMaterial = async () => {
    if (selectedEnvironmentIndex === null) return;

    // Verificar stock antes de agregar
    if (newMaterial.product && typeof newMaterial.product.stock === 'number') {
      const cantidadPresupuestada = newMaterial.usar_cantidad_manual 
        ? (newMaterial.cantidad_manual || 0) 
        : (newMaterial.cantidad_calculada || newMaterial.unidades);
      
      if (cantidadPresupuestada > newMaterial.product.stock) {
        setAlertStock(
          `Estás por presupuestar más de lo que hay en stock (${newMaterial.product.stock}). Revisa los próximos ingresos.`
        );
        // No hacer return, solo mostrar la alerta
      } else {
        setAlertStock(null);
      }
    }

    try {
      // Si tiene estructura, agregar también los materiales de estructura
      if (newMaterial.con_estructura && newMaterial.product?.estructura_m2) {
        await addStructureMaterials(selectedEnvironmentIndex, newMaterial.product, newMaterial.metros_cuadrados || 1);
      }

      // Agregar el material principal
      let materialToAdd = {
        ...newMaterial,
        unidades: newMaterial.usar_cantidad_manual ? (newMaterial.cantidad_manual || 0) : (newMaterial.cantidad_calculada || newMaterial.unidades)
      };
      // Si el material es de grupo ESTRUCTURA, forzar flags y observaciones igual que los auto-agregados
      if (newMaterial.product && newMaterial.product.grupo === 'ESTRUCTURA') {
        materialToAdd = {
          ...materialToAdd,
          metros_cuadrados: undefined,
          con_estructura: false,
          con_desperdicio: false,
          usar_cantidad_manual: false,
          observaciones: 'Estructura para ', // código vacío
        };
      }
      setFormData(prev => {
        const newEnvironments = [...prev.environments];
        newEnvironments[selectedEnvironmentIndex].materials.push(materialToAdd);
        return { ...prev, environments: newEnvironments };
      });

      resetMaterialForm();
    } catch (err) {
      setError('Error al agregar el material');
    }
  };

  // Función para agregar materiales de estructura (copiada de BudgetFormNew)
  const addStructureMaterials = async (envIndex: number, product: Product, metros_cuadrados: number) => {
    if (!product.estructura_m2) return;

    const structureEntries = product.estructura_m2.split(/[;\n]+/);
    
    for (const entry of structureEntries) {
      const trimmedEntry = entry.trim();
      if (!trimmedEntry) continue;
      
      try {
        const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
        const codigoTrimmed = codigo.trim();
        const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
        
        const structureProduct = products.find(p => p.codigo === codigoTrimmed);
        if (!structureProduct) continue;

        const cantidadBase = (newMaterial.product?.grupo === 'PERFIL')
          ? (newMaterial.unidades || 1)
          : (metros_cuadrados || 1);
        const cantidadTotal = cantidadPorM2 * cantidadBase;
        const cantidadFinal = Math.ceil(cantidadTotal);
        
        const structureMaterial: MaterialFormData = {
          product_id: structureProduct.id,
          product: structureProduct,
          es_in_situ: false,
          descripcion: structureProduct.codigo,
          codigo: structureProduct.codigo,
          unidades: cantidadFinal,
          precio_unitario: structureProduct.precio_unitario || 0,
          currency: (structureProduct.currency === 'USD' || structureProduct.currency === 'ARS') ? structureProduct.currency : 'USD',
          metros_cuadrados: undefined, // ✅ Items de estructura NO tienen m2
          cantidad_calculada: cantidadFinal,
          usar_cantidad_manual: false,
          con_desperdicio: false,
          con_estructura: false,
          observaciones: `Estructura para ${product.codigo}`
        };
        
        setFormData(prev => {
          const newEnvironments = [...prev.environments];
          newEnvironments[envIndex].materials.push(structureMaterial);
          return { ...prev, environments: newEnvironments };
        });
      } catch (error) {
        console.error('Error processing structure entry:', trimmedEntry, error);
      }
    }
  };

  const handleDeleteMaterial = async (envIndex: number, matIndex: number) => {
    try {
      setSaving(true);
      const material = formData.environments[envIndex].materials[matIndex];
      
      console.log(`🗑️ Intentando eliminar material:`, {
        envIndex,
        matIndex,
        material,
        hasId: !!material.id,
        id: material.id
      });
      
      if (material.id && material.id > 0) {
        // Eliminar del backend si tiene ID válido
        console.log(`🔥 Eliminando material del backend con ID: ${material.id}`);
        await BudgetApiService.deleteEnvironmentMaterial(material.id);
        console.log(`✅ Material "${material.descripcion}" eliminado del backend`);
      } else {
        console.log(`⚠️ Material sin ID válido, solo eliminando del estado local:`, material);
      }

      // Eliminar del estado local
      setFormData(prev => {
        const newEnvironments = [...prev.environments];
        newEnvironments[envIndex].materials = newEnvironments[envIndex].materials.filter((_, i) => i !== matIndex);
        return { ...prev, environments: newEnvironments };
      });
      
      console.log(`✅ Material eliminado del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando material:', error);
      setError('Error al eliminar el material');
    } finally {
      setSaving(false);
    }
  };

  // Funciones para manejo de instalación
  const handleAddInstallation = () => {
    if (selectedEnvironmentIndex === null) return;

    // Validar según el modo
    if (installationMode === 'catalog' && !newInstallation.installation_service_id) {
      return;
    }
    if (installationMode === 'custom' && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2)) {
      return;
    }

    // Calcular el precio
    let mano_obra_m2 = newInstallation.mano_obra_m2 || 0;
    
    // Solo usar el precio del catálogo si estamos en modo catálogo y el usuario no editó el campo
    if (installationMode === 'catalog') {
      if (!newInstallation.mano_obra_m2 || newInstallation.mano_obra_m2 === 0) {
        if (newInstallation.installation_service && newInstallation.tipo_instalacion) {
          if (newInstallation.tipo_instalacion === 'standard') {
            mano_obra_m2 = newInstallation.installation_service.precio_standard || 0;
          } else if (newInstallation.tipo_instalacion === 'compleja') {
            mano_obra_m2 = newInstallation.installation_service.precio_complejo || 0;
          }
        }
      }
    }

    // Calcular subtotal - para unidad global no multiplicar por cantidad
    const cantidad = newInstallation.cantidad_manual || 1;
    const esGlobal = installationMode === 'custom' && newInstallation.unidad?.toLowerCase() === 'global';
    const subtotal = esGlobal ? mano_obra_m2 : mano_obra_m2 * cantidad;

    // Preparar la instalación
    // Para custom: guardar unidad seleccionada
    // Para catálogo: NO guardar unidad (se usa la del servicio)
    const installationToAdd = {
      ...newInstallation,
      mano_obra_m2: mano_obra_m2,
      cantidad_manual: cantidad,
      subtotal: subtotal,
      unidad: installationMode === 'custom' ? (newInstallation.unidad || '') : undefined,
      tipo_instalacion: installationMode === 'custom' ? 'custom' : newInstallation.tipo_instalacion
    };

    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      
      // Si es el ambiente general (-1), buscarlo o crearlo
      if (selectedEnvironmentIndex === GENERAL_INSTALLATIONS_INDEX) {
        let generalEnvIndex = newEnvironments.findIndex(env => env.nombre === GENERAL_INSTALLATIONS_ENV_NAME);
        
        if (generalEnvIndex === -1) {
          // Crear el ambiente general
          newEnvironments.push({
            nombre: GENERAL_INSTALLATIONS_ENV_NAME,
            descripcion: 'Instalaciones generales del presupuesto',
            orden: 9999, // Al final
            materials: [],
            installations: [installationToAdd]
          });
        } else {
          // Agregar al ambiente general existente
          newEnvironments[generalEnvIndex].installations = [
            ...newEnvironments[generalEnvIndex].installations,
            installationToAdd
          ];
        }
      } else {
        // Ambiente normal
        newEnvironments[selectedEnvironmentIndex].installations = [
          ...newEnvironments[selectedEnvironmentIndex].installations,
          installationToAdd
        ];
      }
      
      return { ...prev, environments: newEnvironments };
    });
    
    setIsDirty(true); // Marcar que hay cambios sin guardar

    setNewInstallation({
      trabajo: '',
      mano_obra_m2: 0,
      currency: 'ARS',
      cantidad_manual: 1
    });
    setInstallationMode('catalog');
    setSelectedEnvironmentIndex(null);
    setInstallationDialog(false);
  };

  // Deprecated: Esta función ya no se usa porque la eliminación se maneja en la tabla con onClick
  // Se mantiene solo por compatibilidad
  const handleDeleteInstallation = async (envIndex: number, instIndex: number) => {
    try {
      setSaving(true);
      const installation = formData.environments[envIndex].installations?.[instIndex];
      
      console.log(`🗑️ Intentando eliminar instalación del ambiente ${envIndex}:`, {
        envIndex,
        instIndex,
        installation,
        hasId: !!installation?.id,
        id: installation?.id
      });
      
      if (installation?.id && installation.id > 0) {
        // Eliminar del backend si tiene ID válido
        console.log(`🔥 Eliminando instalación del backend con ID: ${installation.id}`);
        await BudgetApiService.deleteEnvironmentInstallation(installation.id);
        console.log(`✅ Instalación eliminada del backend`);
      } else {
        console.log(`⚠️ Instalación sin ID válido, solo eliminando del estado local:`, installation);
      }

      // Eliminar del estado local
      setFormData(prev => {
        const newEnvironments = [...prev.environments];
        newEnvironments[envIndex].installations = newEnvironments[envIndex].installations.filter((_, idx) => idx !== instIndex);
        return { ...prev, environments: newEnvironments };
      });
      
      console.log(`✅ Instalación eliminada del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando instalación:', error);
      setError('Error al eliminar la instalación');
    } finally {
      setSaving(false);
    }
  };

  // Funciones para manejo de fletes
  const handleAddFreight = () => {
    setFormData(prev => ({
      ...prev,
      freights: [...prev.freights, { ...newFreight }]
    }));

    setNewFreight({
      tipo_envio: '',
      monto: 0,
      tipo: 'fijo',
      currency: 'ARS'
    });
    setFreightDialog(false);
  };

  const handleDeleteFreight = async (index: number) => {
    try {
      setSaving(true);
      const freight = formData.freights[index];
      
      console.log(`🗑️ Intentando eliminar flete:`, {
        index,
        freight,
        hasId: !!freight.id,
        id: freight.id
      });
      
      if (freight.id && freight.id > 0) {
        // Eliminar del backend si tiene ID válido
        console.log(`🔥 Eliminando flete del backend con ID: ${freight.id}`);
        await BudgetApiService.deleteFreight(freight.id);
        console.log(`✅ Flete "${freight.tipo_envio}" eliminado del backend`);
      } else {
        console.log(`⚠️ Flete sin ID válido, solo eliminando del estado local:`, freight);
      }

      // Eliminar del estado local
      setFormData(prev => ({
        ...prev,
        freights: prev.freights.filter((_, i) => i !== index)
      }));
      
      console.log(`✅ Flete eliminado del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando flete:', error);
      setError('Error al eliminar el flete');
    } finally {
      setSaving(false);
    }
  };

  // Función para resetear formulario de material
  const resetMaterialForm = () => {
    setNewMaterial({
      descripcion: '',
      codigo: '',
      unidades: 0,
      precio_unitario: 0,
      currency: 'USD',
      es_in_situ: false,
      metros_cuadrados: 1,
      usar_cantidad_manual: false,
      con_desperdicio: true,
      con_estructura: false,
      observaciones: ''
    });
    setSelectedEnvironmentIndex(null);
    setMaterialDialog(false);
  };

  // Funciones para crear nueva versión
  const handleCreateVersion = async () => {
    if (!formData.id || !newVersionData.numero_version) return;
    setSaving(true);
    try {
      // Buscar la versión cerrada seleccionada
      const selectedVersion = formData.versions.find(
        v => v.estado === 'closed' && String(v.version_number) === String(newVersionData.numero_version)
      );
      if (!selectedVersion) {
        setError('Debes seleccionar una versión cerrada válida');
        setSaving(false);
        return;
      }
      // Guardar cambios pendientes de la versión actual antes de clonar,
      // para no perderlos cuando se recargue y se cambie a la nueva versión
      await updateAllBudgetData();
      // Clonar y obtener la nueva versión
      const newVersion = await BudgetApiService.cloneBudgetVersion(formData.id, selectedVersion.id);
      setNewVersionDialog(false);
      setNewVersionData({
        titulo: '',
        vendedor: '',
        email_vendedor: '',
        tel_vendedor: '',
        condiciones_contratacion: '',
        numero_version: '',
        nombre: '',
        condiciones: ''
      });
      setSuccess('Nueva versión creada exitosamente');
      // Recargar datos y seleccionar la nueva versión como actual
      await loadBudgetData();
      // Forzar selección de la nueva versión como actual
      setTimeout(async () => {
        try {
          const versionWithDetails = await BudgetApiService.getBudgetVersion(formData.id, newVersion.id);
          setCurrentVersion(versionWithDetails);
          setFormData(prev => ({
            ...prev,
            currentVersion: versionWithDetails
          }));
        } catch (e) {
          // Si falla, al menos los datos están recargados
        }
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al crear la versión');
    } finally {
      setSaving(false);
    }
  };

  // Función para cambiar estado del presupuesto
  const handleStatusChange = async () => {
    if (!budget || !currentVersion || !selectedStatus) return;
    setSaving(true);
    try {
      // Guardar cambios antes de cambiar estado
      await updateAllBudgetData();
  await BudgetApiService.updateBudgetVersion(budget.id, currentVersion.id, { estado: selectedStatus as any });
      setSuccess(`Estado cambiado a ${getStatusLabel(selectedStatus)}`);
      setStatusDialog(false);
      setSelectedStatus('');
      await loadBudgetData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cambiar el estado');
    } finally {
      setSaving(false);
    }
  };

  // Función para borrar presupuesto
  const handleDeleteBudget = async () => {
    if (!budget) return;
    
    setSaving(true);
    try {
      await BudgetApiService.deleteBudget(budget.id);
      setSuccess('Presupuesto eliminado exitosamente');
      navigate('/budgets');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al eliminar el presupuesto');
      setSaving(false);
    }
  };

  // Funciones de utilidad
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'sent': return 'primary';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'closed': return 'info';
      case 'sold': return 'success';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Borrador';
      case 'sent': return 'Enviado';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'Rechazado';
      case 'closed': return 'Cerrado';
      case 'sold': return 'Vendido';
      case 'lost': return 'Perdido';
      default: return status;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    // Siempre mostrar símbolo antes del valor, miles con punto y decimales con coma
    if (currency === 'USD') {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // ==========================================
  // FUNCIÓN DE DESCARGA DE PDF
  // ==========================================

  /**
   * Descargar PDF de una versión específica
   */
  const handleDownloadPdf = async (versionId?: number) => {
    if (!budget) return;

    try {
      setSaving(true);
      setError(null);
      
      // Usar la versión especificada o la actual
      const targetVersionId = versionId || currentVersion?.id;
      const isCurrentVersion = !versionId || targetVersionId === currentVersion?.id;
      
      console.log(`📄 Descargando PDF del presupuesto ${budget.id}, versión ${targetVersionId}...`);
      
      // Si es la versión actual y está en draft, guardar cambios y cambiar a closed
      if (isCurrentVersion && currentVersion?.estado === 'draft') {
        // ✅ PRIMERO: Guardar todos los cambios pendientes si hay dirty state
        if (isDirty) {
          console.log('💾 Guardando cambios pendientes antes de cerrar la versión...');
          await updateAllBudgetData();
          console.log('✅ Cambios guardados correctamente');
        }
        
        // ✅ SEGUNDO: Cambiar estado a closed
        console.log('🔒 Cambiando versión actual a estado "closed"...');
        await BudgetApiService.updateBudgetVersion(budget.id, currentVersion.id, { estado: 'closed' });
        console.log('✅ Versión cambiada a "closed"');
      }
      
      const blob = await BudgetApiService.exportPdf(budget.id, targetVersionId);
      
      // Crear nombre de archivo: presupuesto_(nombre-proyecto)_(cliente)_v(version).pdf
      const versionNumber = versionId 
        ? versions.find(v => v.id === versionId)?.version_number 
        : currentVersion?.version_number;
      // Sanitizar nombre del proyecto y cliente para usar como nombre de archivo
      const proyectoSanitizado = budget.proyecto.replace(/\s+/g, '-').replace(/\//g, '-');
      const clienteSanitizado = budget.cliente?.replace(/\s+/g, '-').replace(/\//g, '-') || '';
      const fileName = clienteSanitizado 
        ? `presupuesto_${proyectoSanitizado}_${clienteSanitizado}_v${versionNumber || 'latest'}.pdf`
        : `presupuesto_${proyectoSanitizado}_v${versionNumber || 'latest'}.pdf`;
      
      // Crear link de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF descargado correctamente');
      setSuccess('PDF descargado correctamente. La versión se ha cerrado.');
      
      // Si cambiamos el estado, recargar datos para actualizar la UI
      if (isCurrentVersion && currentVersion?.estado === 'draft') {
        console.log('🔄 Recargando datos del presupuesto...');
        await loadBudgetData();
        setIsDirty(false); // Limpiar dirty flag ya que recargamos todo
      }
      
    } catch (error) {
      console.error('❌ Error descargando PDF:', error);
      setError('Error al descargar el PDF. Intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // FUNCIONES DE ACTUALIZACIÓN DE DATOS
  // ==========================================

  /**
   * Actualizar datos básicos del presupuesto
   */
  const updateBudgetBasicData = async () => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      
      // Actualizar datos del presupuesto principal
      const budgetUpdateData = {
        proyecto: formData.proyecto,
        cliente: formData.cliente,
        responsable: formData.responsable,
        email_cliente: formData.email_cliente,
        tel_cliente: formData.tel_cliente,
        status: formData.status
      };

      await BudgetApiService.updateBudget(budget.id, budgetUpdateData);

      // Actualizar datos de la versión actual
      const versionUpdateData = {
        titulo: formData.currentVersion?.titulo || '',
        vendedor: formData.currentVersion?.vendedor || '',
        email_vendedor: formData.currentVersion?.email_vendedor || '',
        tel_vendedor: formData.currentVersion?.tel_vendedor || '',
        condiciones_contratacion: formData.currentVersion?.condiciones_contratacion || '',
        condiciones_adicionales: formData.currentVersion?.condiciones_adicionales || '',
        descuento_materiales: formData.descuento_materiales || 0,
        descuento_instalacion: formData.descuento_instalacion || 0,
        descuento_fletes: formData.descuento_fletes || 0
      };

      await BudgetApiService.updateBudgetVersion(budget.id, currentVersion.id, versionUpdateData);

      console.log('✅ Datos básicos del presupuesto actualizados');
      
    } catch (error) {
      console.error('❌ Error actualizando datos básicos:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actualizar un ambiente específico
   */
  const updateEnvironment = async (envIndex: number) => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      const environment = formData.environments[envIndex];
      
      if (environment.id) {
        // Actualizar ambiente existente
        const envUpdateData = {
          nombre: environment.nombre,
          descripcion: environment.descripcion || '',
          orden: environment.orden,
          descuento_materiales: environment.descuento_materiales || 0
        };

        await BudgetApiService.updateEnvironment(environment.id, envUpdateData);
        console.log(`✅ Ambiente "${environment.nombre}" actualizado`);
      } else {
        // Crear nuevo ambiente si no tiene ID
        const envCreateData: EnvironmentCreate = {
          nombre: environment.nombre,
          descripcion: environment.descripcion || '',
          orden: environment.orden,
          descuento_materiales: environment.descuento_materiales || 0
        };

        const newEnv = await BudgetApiService.createEnvironment(budget.id, currentVersion.id, envCreateData);
        
        // Actualizar el ID en el estado local
        setFormData(prev => {
          const newEnvironments = [...prev.environments];
          newEnvironments[envIndex].id = newEnv.id;
          return { ...prev, environments: newEnvironments };
        });

        console.log(`✅ Nuevo ambiente "${environment.nombre}" creado con ID: ${newEnv.id}`);
      }
      
    } catch (error) {
      console.error('❌ Error actualizando ambiente:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actualizar un material específico
   */
  const updateEnvironmentMaterial = async (envIndex: number, matIndex: number) => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      const environment = formData.environments[envIndex];
      const material = environment.materials[matIndex];

      if (!environment.id) {
        throw new Error('El ambiente debe ser creado antes de actualizar sus materiales');
      }

      if (material.id) {
        // Actualizar material existente
        const matUpdateData = {
          descripcion: material.descripcion,
          codigo: material.codigo,
          unidades: material.unidades,
          metros2: material.metros_cuadrados || undefined, // ✅ Solo incluir si tiene valor (materiales de estructura no tienen m2)
          precio_unitario: material.precio_unitario,
          monto: material.unidades * material.precio_unitario,
          currency: material.currency,
          observaciones: material.observaciones,
          con_desperdicio: material.con_desperdicio || false,
          con_estructura: material.con_estructura || false
        };

        await BudgetApiService.updateEnvironmentMaterial(material.id, matUpdateData);
        console.log(`✅ Material "${material.descripcion}" actualizado`);
      } else {
        // Crear nuevo material si no tiene ID
        const matCreateData: EnvironmentMaterialCreate = {
          product_id: material.product_id,
          descripcion: material.descripcion,
          codigo: material.codigo,
          unidades: material.unidades,
          metros2: material.metros_cuadrados || undefined, // ✅ Solo incluir si tiene valor (materiales de estructura no tienen m2)
          precio_unitario: material.precio_unitario,
          monto: material.unidades * material.precio_unitario,
          currency: material.currency,
          es_in_situ: material.es_in_situ,
          observaciones: material.observaciones,
          con_desperdicio: material.con_desperdicio || false,
          con_estructura: material.con_estructura || false
        };

        const newMaterial = material.es_in_situ 
          ? await BudgetApiService.createEnvironmentMaterial(environment.id, matCreateData)
          : await BudgetApiService.createEnvironmentMaterialFromProduct(environment.id, material.product_id!, matCreateData);

        // Actualizar el ID en el estado local
        setFormData(prev => {
          const newEnvironments = [...prev.environments];
          newEnvironments[envIndex].materials[matIndex].id = newMaterial.id;
          return { ...prev, environments: newEnvironments };
        });

        console.log(`✅ Nuevo material "${material.descripcion}" creado con ID: ${newMaterial.id}`);
      }
      
    } catch (error) {
      console.error('❌ Error actualizando material:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actualizar instalaciones de un ambiente
   */
  const updateEnvironmentInstallation = async (envIndex: number) => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      const environment = formData.environments[envIndex];
      const installations = environment.installations;

      if (!environment.id) {
        throw new Error('El ambiente debe ser creado antes de actualizar sus instalaciones');
      }

      if (!installations || installations.length === 0) {
        return; // No hay instalaciones que actualizar
      }

      // Actualizar o crear cada instalación
      for (let i = 0; i < installations.length; i++) {
        const installation = installations[i];

        // Usar el precio editado (mano_obra_m2) si existe, sino usar el del catálogo como fallback
        let mano_obra_m2 = installation.mano_obra_m2;
        
        // Solo usar el precio del catálogo si el usuario no editó el campo
        if (!installation.mano_obra_m2 || installation.mano_obra_m2 === 0) {
          if (installation.installation_service && installation.tipo_instalacion) {
            if (installation.tipo_instalacion === 'standard') {
              mano_obra_m2 = installation.installation_service.precio_standard || 0;
            } else if (installation.tipo_instalacion === 'compleja') {
              mano_obra_m2 = installation.installation_service.precio_complejo || 0;
            }
          }
        }

        // Calcular subtotal SOLO con los campos de instalación: mano_obra_m2 * cantidad_manual
        // Para unidad global, no multiplicar por cantidad
        const cantidad = installation.cantidad_manual ?? 1;
        const esCustom = installation.tipo_instalacion === 'custom' || !installation.installation_service_id;
        const esGlobal = esCustom && installation.unidad?.toLowerCase() === 'global';
        const subtotal = esGlobal ? (mano_obra_m2 ?? 0) : (mano_obra_m2 ?? 0) * cantidad;

        // Para la unidad: solo guardar si es custom (tiene valor propio)

        if (installation.id) {
          // Actualizar instalación existente
          const instUpdateData: any = {
            trabajo: installation.trabajo,
            descripcion_trabajo: installation.descripcion_trabajo,
            mano_obra_m2: mano_obra_m2,
            subtotal: subtotal,
            currency: installation.currency,
            cantidad_manual: installation.cantidad_manual ?? 1,
            installation_service_id: installation.installation_service_id,
            tipo_instalacion: installation.tipo_instalacion
          };
          // Solo incluir unidad para custom
          if (esCustom) {
            instUpdateData.unidad = installation.unidad || '';
          }

          await BudgetApiService.updateEnvironmentInstallation(installation.id, instUpdateData);
          console.log(`✅ Instalación ${i + 1} del ambiente "${environment.nombre}" actualizada`);
        } else {
          // Crear nueva instalación si no tiene ID
          const instCreateData: any = {
            trabajo: installation.trabajo,
            descripcion_trabajo: installation.descripcion_trabajo,
            mano_obra_m2: mano_obra_m2,
            subtotal: subtotal,
            currency: installation.currency,
            cantidad_manual: installation.cantidad_manual ?? 1,
            installation_service_id: installation.installation_service_id,
            tipo_instalacion: installation.tipo_instalacion
          };
          // Solo incluir unidad para custom
          if (esCustom) {
            instCreateData.unidad = installation.unidad || '';
          }

          const newInstallation = await BudgetApiService.createEnvironmentInstallation(environment.id, instCreateData);

          // Actualizar el ID en el estado local
          setFormData(prev => {
            const newEnvironments = [...prev.environments];
            if (newEnvironments[envIndex].installations[i]) {
              newEnvironments[envIndex].installations[i].id = newInstallation.id;
            }
            return { ...prev, environments: newEnvironments };
          });

          console.log(`✅ Nueva instalación ${i + 1} creada para ambiente "${environment.nombre}" con ID: ${newInstallation.id}`);
        }
      }
      
    } catch (error) {
      console.error('❌ Error actualizando instalaciones:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actualizar un flete específico
   */
  const updateFreight = async (freightIndex: number) => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      const freight = formData.freights[freightIndex];

      if (freight.id) {
        // Actualizar flete existente
        const freightUpdateData = {
          tipo_envio: freight.tipo_envio,
          descripcion_detalle: freight.descripcion_detalle,
          monto: freight.monto,
          currency: freight.currency
        };

        await BudgetApiService.updateFreight(freight.id, freightUpdateData);
        console.log(`✅ Flete "${freight.tipo_envio}" actualizado`);
      } else {
        // Crear nuevo flete si no tiene ID
        const freightCreateData: FreightCreate = {
          tipo_envio: freight.tipo_envio,
          descripcion_detalle: freight.descripcion_detalle,
          monto: freight.monto,
          currency: freight.currency
        };

        const newFreight = await BudgetApiService.createFreight(budget.id, currentVersion.id, freightCreateData);

        // Actualizar el ID en el estado local
        setFormData(prev => {
          const newFreights = [...prev.freights];
          newFreights[freightIndex].id = newFreight.id;
          return { ...prev, freights: newFreights };
        });

        console.log(`✅ Nuevo flete "${freight.tipo_envio}" creado con ID: ${newFreight.id}`);
      }
      
    } catch (error) {
      console.error('❌ Error actualizando flete:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actualizar todos los datos del presupuesto
   */
  const updateAllBudgetData = async () => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      console.log('🔄 Iniciando actualización completa del presupuesto...');

      // 1. Actualizar datos básicos
      await updateBudgetBasicData();

      // 2. Actualizar todos los ambientes
      for (let envIndex = 0; envIndex < formData.environments.length; envIndex++) {
        await updateEnvironment(envIndex);

        // 3. Actualizar todos los materiales de cada ambiente
        const environment = formData.environments[envIndex];
        for (let matIndex = 0; matIndex < environment.materials.length; matIndex++) {
          await updateEnvironmentMaterial(envIndex, matIndex);
        }

        // 4. Actualizar instalaciones del ambiente si existen
        if (environment.installations && environment.installations.length > 0) {
          await updateEnvironmentInstallation(envIndex);
        }
      }

      // 5. Actualizar todos los fletes
      for (let freightIndex = 0; freightIndex < formData.freights.length; freightIndex++) {
        await updateFreight(freightIndex);
      }

      console.log('✅ Actualización completa del presupuesto finalizada');
      setSuccess('Todos los cambios han sido guardados correctamente');
      
    } catch (error) {
      console.error('❌ Error en actualización completa:', error);
      setError('Error al guardar los cambios. Intenta nuevamente.');
      throw error;
    } finally {
      setSaving(false);
    }
  };


  // ==========================================
  // GUARDAR AUTOMÁTICAMENTE AL SALIR O REFRESCAR
  // ==========================================
  // ...existing code...

  // Guardar al refrescar/cerrar pestaña SOLO si hay cambios
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        updateAllBudgetData();
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Guardar al navegar fuera del detalle (cambiar de ruta) SOLO si hay cambios
  useEffect(() => {
    const handlePopState = () => {
      if (isDirty) {
        updateAllBudgetData();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty]);

  // Total de materiales SIN descuentos
  const getTotalMaterials = () => {
    return formData.environments.reduce((total, env) => {
      return total + env.materials.reduce((envTotal, mat) => {
        const amount = mat.unidades * mat.precio_unitario;
        return envTotal + (mat.currency === 'USD' ? amount : amount / 100); // Conversión simplificada
      }, 0);
    }, 0);
  };

  // Total de materiales CON descuentos por ambiente aplicados
  const getTotalMaterialsWithDiscounts = () => {
    return formData.environments.reduce((total, env) => {
      const envTotal = env.materials.reduce((sum, mat) => {
        const amount = mat.unidades * mat.precio_unitario;
        return sum + (mat.currency === 'USD' ? amount : amount / 100);
      }, 0);
      const descuento = env.descuento_materiales || 0;
      return total + (envTotal * (1 - descuento / 100));
    }, 0);
  };


  // Helper para obtener solo el total ARS de instalaciones (para cálculos)
  const getTotalInstallationsARS = () => {
    let totalARS = 0;
    formData.environments.forEach(env => {
      if (!env.installations || env.installations.length === 0) return;
      env.installations.forEach(installation => {
        let subtotal = 0;
        let moneda = 'ARS';
        if (installation.installation_service && installation.tipo_instalacion) {
          // Usar el precio editado (mano_obra_m2) si existe, sino usar el precio del catálogo
          const precio = installation.mano_obra_m2 
            ? installation.mano_obra_m2 
            : (installation.tipo_instalacion === 'standard'
              ? installation.installation_service.precio_standard
              : installation.installation_service.precio_complejo);
          moneda = installation.tipo_instalacion === 'standard'
            ? installation.installation_service.moneda_standard
            : installation.installation_service.moneda_compleja;
          if (precio && moneda === 'ARS') {
            subtotal = precio * (installation.cantidad_manual ?? 1);
          }
        } else if (installation.mano_obra_m2 && (installation.currency === 'ARS' || !installation.currency)) {
          // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
          const esGlobal = installation.unidad?.toLowerCase() === 'global';
          subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
        }
        totalARS += subtotal;
      });
    });
    return totalARS;
  };

  const getTotalFreights = () => {
    // Calcular subtotal de materiales + instalaciones (solo ARS) para aplicar porcentajes
    const subtotalMaterialsAndInstallations = getTotalMaterials() + getTotalInstallationsARS();
    return formData.freights.reduce((total, freight) => {
      let amount: number;
      if (freight.tipo === 'porcentaje') {
        // Para porcentaje, aplicar el monto como porcentaje del subtotal
        amount = (subtotalMaterialsAndInstallations * freight.monto) / 100;
      } else {
        amount = freight.monto;
      }
      return total + amount;
    }, 0);
  };

  // Suma los subtotales de instalación por moneda (ARS/USD/other), igual que la tabla de instalaciones
  const getTotalInstallations = (): string => {
    const totales = { ARS: 0, USD: 0, other: 0 };
    formData.environments.forEach(env => {
      if (!env.installations || env.installations.length === 0) return;
      env.installations.forEach(installation => {
        let subtotal = 0;
        let moneda = 'ARS';
        let precio = 0;
        if (installation.installation_service && installation.tipo_instalacion) {
          // Usar el precio editado (mano_obra_m2) si existe, sino usar el del catálogo
          precio = installation.mano_obra_m2
            ? installation.mano_obra_m2
            : (installation.tipo_instalacion === 'standard'
              ? installation.installation_service.precio_standard
              : installation.installation_service.precio_complejo);
          moneda = installation.tipo_instalacion === 'standard'
            ? installation.installation_service.moneda_standard
            : installation.installation_service.moneda_compleja;
        } else if (installation.mano_obra_m2) {
          precio = installation.mano_obra_m2;
          moneda = installation.currency || 'ARS';
        }
        // Para instalaciones custom con unidad 'global', no multiplicar por cantidad
        const esCustomGlobal = !installation.installation_service_id && installation.unidad?.toLowerCase() === 'global';
        const cantidad = esCustomGlobal ? 1 : (installation.cantidad_manual ?? 1);
        subtotal = (precio ?? 0) * cantidad;
        if (moneda === 'ARS') {
          totales.ARS += subtotal;
        } else if (moneda === 'USD') {
          totales.USD += subtotal;
        } else {
          totales.other += subtotal;
        }
      });
    });
    // Devuelve string igual que la tabla de instalaciones
    const parts = [];
    if (totales.ARS > 0) parts.push(formatInstallationCurrency(totales.ARS, 'ARS'));
    if (totales.USD > 0) parts.push(formatInstallationCurrency(totales.USD, 'USD'));
    if (totales.other > 0) parts.push(`${totales.other.toFixed(2)}`);
    return parts.join(' + ') || formatInstallationCurrency(0, 'ARS');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!budget) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Presupuesto no encontrado
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ width: '80%', margin: '0 auto' }}>
      {/* Header con botón de volver y acciones */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={async () => {
            try {
              // Guardar cambios antes de navegar
              await updateAllBudgetData();
            } catch (error) {
              console.error('Error al guardar cambios:', error);
              // Continuar navegando aunque haya error
            } finally {
              navigate('/budgets');
            }
          }}
        >
          Volver a Presupuestos
        </Button>
        
        {/* Botones de acción */}
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<Download />}
            variant="outlined"
            onClick={async () => {
              try {
                // Guardar cambios primero
                await updateAllBudgetData();
                // Luego descargar la última versión (versión actual)
                await handleDownloadPdf();
              } catch (error) {
                setError('Error al guardar cambios o descargar PDF.');
              }
            }}
          >
            Descargar PDF
          </Button>
          {canChangeStatus && (
            <Button
              startIcon={<Edit />}
              variant="contained"
              onClick={() => setStatusDialog(true)}
            >
              Cambiar Estado
            </Button>
          )}
          {showVersionActions && (
            <>
              {!isReadOnly && user?.role === 'admin' && (
                <Button
                  startIcon={<Delete />}
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteDialog(true)}
                >
                  Eliminar
                </Button>
              )}
              <Button
                startIcon={<Add />}
                variant="contained"
                color="primary"
                onClick={() => setNewVersionDialog(true)}
                disabled={isReadOnly}
              >
                Crear Nueva Versión
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Título centrado */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          {formData.proyecto}
        </Typography>
        <Box display="flex" justifyContent="center" gap={3} mt={2}>
          <Typography variant="h6" color="text.secondary">
            Código: {formData.code}
          </Typography>
          {currentVersion && (
            <>
              <Typography variant="h6" color="text.secondary">
                Versión: {currentVersion.version_number || 'v1.0'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Fecha: {currentVersion.created_at ? new Date(currentVersion.created_at).toLocaleDateString() : '-'}
              </Typography>
            </>
          )}
        </Box>
        <Box mt={2}>
          <Chip
            label={getStatusLabel(formData.currentVersion?.estado || 'draft')}
            color={getStatusColor(formData.currentVersion?.estado || 'draft')}
            variant="filled"
            size="medium"
          />
        </Box>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Card de Información General - Proyecto/Cliente vs Vendedor */}
        <Card>
          <CardHeader 
            title="Información del Proyecto"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Business color="primary" />}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }} sx={{ mx: 3 }}>
              {/* Información del Proyecto y Cliente */}
              <Box flex="1">
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Proyecto y Cliente
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Nombre del Proyecto"
                    value={formData.proyecto}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, proyecto: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly ||isFieldsReadOnly }}
                  />
                  <TextField
                    label="Cliente"
                    value={formData.cliente}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, cliente: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  <TextField
                    label="Responsable"
                    value={formData.responsable}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, responsable: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  <TextField
                    label="Email Cliente"
                    value={formData.email_cliente}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, email_cliente: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  <TextField
                    label="Teléfono Cliente"
                    value={formData.tel_cliente}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, tel_cliente: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                </Stack>
              </Box>

              {/* Información del Vendedor - Alineada a la derecha */}
              <Box flex="1" textAlign="right">
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Datos del Vendedor
                </Typography>
                {currentVersion ? (
                  <Stack spacing={2}>
                    <TextField
                      label="Vendedor"
                      value={formData.currentVersion?.vendedor || ''}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        setFormData(prev => ({
                          ...prev,
                          currentVersion: prev.currentVersion ? { ...prev.currentVersion, vendedor: e.target.value } : null
                        }));
                        setIsDirty(true);
                      }}
                      fullWidth
                      InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                    />
                    <TextField
                      label="Email Vendedor"
                      value={formData.currentVersion?.email_vendedor || ''}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        setFormData(prev => ({
                          ...prev,
                          currentVersion: prev.currentVersion ? { ...prev.currentVersion, email_vendedor: e.target.value } : null
                        }));
                        setIsDirty(true);
                      }}
                      fullWidth
                      InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                    />
                    <TextField
                      label="Teléfono Vendedor"
                      value={formData.currentVersion?.tel_vendedor || ''}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        setFormData(prev => ({
                          ...prev,
                          currentVersion: prev.currentVersion ? { ...prev.currentVersion, tel_vendedor: e.target.value } : null
                        }));
                        setIsDirty(true);
                      }}
                      fullWidth
                      InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                    />
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mx: 3 }}>
                    <Typography variant="body1">No hay versión actual disponible</Typography>
                  </Alert>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Card de Ambientes y Materiales - Reutilizando estilo del form */}
        <Card>
          <CardHeader 
            title="Ambientes y Materiales"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Home color="primary" />}
            subheader={`${formData.environments.length} ambiente(s) configurado(s)`}
            subheaderTypographyProps={{ variant: 'body1' }}
            sx={{ pb: 1 }}
            action={
              !(isReadOnly || isFieldsReadOnly) && (
                <Box sx={{ mr: 5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setEnvironmentDialog(true)}
                  >
                    Agregar Ambiente
                  </Button>
                </Box>
              )
            }
          />
          <CardContent sx={{ p: 3 }}>
            {formData.environments.length > 0 ? (
              <Box>
                <Box display="flex" justifyContent="center">
                  <TableContainer component={Paper} sx={{ mb: 3, maxWidth: 'calc(100% - 40px)' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: '110px', maxWidth: '110px', minWidth: '110px' }}>Tipo</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Material</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Precio Unitario</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Unidades</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.environments
                        .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                        .map((env, envIndex) => {
                          // Obtener el índice real en formData.environments
                          const actualEnvIndex = formData.environments.findIndex(e => e === env);
                          return (
                        <React.Fragment key={actualEnvIndex}>
                          {/* Fila del ambiente */}
                          <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                            <TableCell align="center">
                              {editingEnvironmentId === env.id ? (
                                // Modo edición: TextField con botones
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                  <TextField
                                    size="small"
                                    value={editingEnvironmentName}
                                    onChange={(e) => setEditingEnvironmentName(e.target.value)}
                                    autoFocus
                                    disabled={savingEnvironmentName}
                                    sx={{ minWidth: 200 }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleSaveEnvironmentName(env.id!)}
                                    disabled={savingEnvironmentName || !editingEnvironmentName.trim()}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleCancelEditEnvironmentName}
                                    disabled={savingEnvironmentName}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Box>
                              ) : (
                                // Modo visualización: Nombre con ícono de editar
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    📍 {env.nombre}
                                  </Typography>
                                  {!(isReadOnly || isFieldsReadOnly) && env.id && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleStartEditEnvironmentName(env.id!, env.nombre)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                {env.descripcion || 'Sin descripción'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary">
                                {env.materials.length} material(es)
                              </Typography>
                            </TableCell>
                            <TableCell align="center"></TableCell>
                            <TableCell align="center"></TableCell>
                            <TableCell align="center"></TableCell>
                            <TableCell align="center">
                              {!(isReadOnly || isFieldsReadOnly) && (
                                <Box display="flex" gap={1} justifyContent="center">
                                  <Button
                                    size="small"
                                    startIcon={<Add />}
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedEnvironmentIndex(actualEnvIndex);
                                      setMaterialDialog(true);
                                    }}
                                  >
                                    Material
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteEnvironment(actualEnvIndex)}
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                          
                          {/* Filas de materiales */}
                          {env.materials.length > 0 ? (
                            env.materials.map((material, matIndex) => (
                              <TableRow key={`material-${actualEnvIndex}-${matIndex}`}>
                                <TableCell align="center" sx={{ width: '200px', maxWidth: '200px', minWidth: '200px' }}>
                                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                    <Box display="flex" gap={0.5}>
                                      {material.es_in_situ ? (
                                        <Chip label="Manual" size="small" color="warning" />
                                      ) : material.product?.descuento === -1 ? (
                                        <Chip label="Outlet" size="small" color="warning" />
                                      ) : (
                                        <Chip label="Catálogo" size="small" color="info" />
                                      )}
                                      {/* Chip de Estructura solo para artículos de estructura (no el principal) */}
                                      {material.observaciones && material.observaciones.toLowerCase().includes('estructura para') && (
                                        <Chip label="Estructura" size="small" color="success" variant="outlined" />
                                      )}
                                      {/* Chip de Desperdicio solo para el principal */}
                                      {material.con_desperdicio && (!material.observaciones || !material.observaciones.toLowerCase().includes('estructura para')) && (
                                        <Chip label="Desperdicio" size="small" color="secondary" variant="outlined" />
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="left">
                                  <Typography variant="body2" fontWeight="medium">
                                    {material.descripcion}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Código: {material.codigo}
                                  </Typography>
                                  {material.observaciones && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {material.observaciones}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="medium">
                                    {formatCurrency(material.precio_unitario, material.currency)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {!(isReadOnly || isFieldsReadOnly) ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={material.unidades}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value) || 1;
                                        if (newValue < 1) return; // Validación mínima
                                        
                                        setFormData(prev => {
                                          const newEnvironments = [...prev.environments];
                                          newEnvironments[actualEnvIndex].materials[matIndex].unidades = newValue;
                                          return { ...prev, environments: newEnvironments };
                                        });
                                        setIsDirty(true);
                                      }}
                                      sx={{ width: '90px' }}
                                      inputProps={{ 
                                        min: 1, 
                                        style: { textAlign: 'right', padding: '8px' } 
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body2" fontWeight="medium">
                                      {material.unidades}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    {formatCurrency(material.unidades * material.precio_unitario, material.currency)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {!(isReadOnly || isFieldsReadOnly) && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteMaterial(actualEnvIndex, matIndex)}
                                      color="error"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No hay materiales agregados
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          
                          {/* Subtotal del ambiente */}
                          <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
                            <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                              Subtotal Ambiente:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {env.materials.reduce((total, mat) => total + mat.unidades, 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              <Box>
                                <Typography variant="body2">
                                  {formatCurrency(env.materials
                                    .filter(m => m.currency === 'USD')
                                    .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0), 'USD')}
                                </Typography>
                                {env.materials.some(m => m.currency === 'ARS') && (
                                  <Typography variant="body2">
                                    {formatCurrency(env.materials
                                      .filter(m => m.currency === 'ARS')
                                      .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0), 'AR')}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>

                          {/* Fila de descuento por ambiente */}
                          <TableRow>
                            <TableCell colSpan={3} align="right">
                              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                                <Typography variant="body2">Descuento Ambiente (%)</Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={Math.round(env.descuento_materiales || 0)}
                                  onChange={(e) => {
                                    const newValue = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                    setFormData(prev => {
                                      const newEnvironments = [...prev.environments];
                                      newEnvironments[actualEnvIndex].descuento_materiales = newValue;
                                      return { ...prev, environments: newEnvironments };
                                    });
                                    setIsDirty(true);
                                  }}
                                  inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                                  disabled={isReadOnly || isFieldsReadOnly}
                                />
                              </Box>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {(env.descuento_materiales || 0) > 0 && (
                                <Typography variant="body2">
                                  - {formatCurrency(
                                    env.materials
                                      .filter(m => m.currency === 'USD')
                                      .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0) * ((env.descuento_materiales || 0) / 100),
                                    'USD'
                                  )}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>

                          {/* Total con descuento del ambiente */}
                          {(env.descuento_materiales || 0) > 0 && (
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                              <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                                Total Ambiente con Descuento:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCurrency(
                                      env.materials
                                        .filter(m => m.currency === 'USD')
                                        .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0) * (1 - (env.descuento_materiales || 0) / 100),
                                      'USD'
                                    )}
                                  </Typography>
                                  {env.materials.some(m => m.currency === 'ARS') && (
                                    <Typography variant="body2" fontWeight="bold">
                                      {formatCurrency(
                                        env.materials
                                          .filter(m => m.currency === 'ARS')
                                          .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario), 0) * (1 - (env.descuento_materiales || 0) / 100),
                                        'AR'
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          )}

                          {/* Línea divisoria entre ambientes */}
                          {envIndex < formData.environments.length - 1 && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ padding: 0 }}>
                                <Divider sx={{ borderBottomWidth: 2, borderColor: '#e0e0e0' }} />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}

                      {/* Fila de TOTAL de materiales */}
                      {formData.environments.length > 0 && (
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell colSpan={4} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            TOTAL MATERIALES:
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {formatCurrency(getTotalMaterialsWithDiscounts(), 'USD')}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>

                {/* Control de descuento global */}
                {!(isReadOnly || isFieldsReadOnly) && formData.environments.filter(e => e.nombre !== GENERAL_INSTALLATIONS_ENV_NAME).length > 0 && (
                  <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', px: 3, py: 1.5, borderRadius: 1, width: 'calc(100% - 40px)', justifyContent: 'center' }}>
                      <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Descuento global:
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={globalDiscountInput}
                        onChange={(e) => setGlobalDiscountInput(e.target.value)}
                        placeholder={commonDiscount === null ? 'Mixto' : ''}
                        inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'center' } }}
                        sx={{ 
                          width: '90px',
                          '& input::placeholder': { 
                            color: 'warning.main', 
                            opacity: 1,
                            fontStyle: 'italic'
                          }
                        }}
                      />
                      <Typography variant="body1" color="text.secondary">%</Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={applyGlobalDiscount}
                        disabled={globalDiscountInput === ''}
                      >
                        Aplicar a todos los ambientes
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Totales de materiales */}
                <Box display="flex" justifyContent="center">
                  <Box display="flex" justifyContent="space-between" alignItems="center" 
                       sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1, mt: 2, width: 'calc(100% - 40px)' }}>
                    <Typography variant="h6" fontWeight="bold">
                      Total Materiales (con descuentos):
                    </Typography>
                  <Box textAlign="right">
                    <Typography variant="h6" fontWeight="bold">
                      {formatCurrency(getTotalMaterialsWithDiscounts(), 'USD')}
                    </Typography>
                    {getTotalMaterials() !== getTotalMaterialsWithDiscounts() && (
                      <Typography variant="body2">
                        Sin descuentos: {formatCurrency(getTotalMaterials(), 'USD')}
                      </Typography>
                    )}
                  </Box>
                </Box>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  No hay ambientes configurados. Agrega un ambiente para comenzar.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Instalación */}
        <Card>
          <CardHeader 
            title="Instalación"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Build color="primary" />}
            subheader={`${formData.environments.filter(env => env.installations && env.installations.length > 0).length} ambiente(s) con instalación`}
            subheaderTypographyProps={{ variant: 'body1' }}
            sx={{ pb: 1 }}
            action={
              !(isReadOnly || isFieldsReadOnly) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  sx={{ ml: -2.5 }}
                  disabled={formData.environments.length === 0}
                  onClick={() => {
                    if (formData.environments.length > 0) {
                      setSelectedEnvironmentIndex(null);
                      setInstallationDialog(true);
                    }
                  }}
                >
                  Agregar Instalación
                </Button>
              )
            }
          />
          <CardContent sx={{ p: 3 }}>
            {formData.environments.some(env => env.installations && env.installations.length > 0) ? (
              <Box>
                <Box display="flex" justifyContent="center">
                  <TableContainer component={Paper} sx={{ maxWidth: 'calc(100% - 40px)' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ambiente</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Trabajo</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio por Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Cantidad de Unidades</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.environments
                          .filter(env => env.installations && env.installations.length > 0)
                          .map((env, envIndex) => {
                            // Calcular área total del ambiente
                            const areaTotal = env.materials.reduce((total, material) => {
                              return total + (material.metros_cuadrados || 0);
                            }, 0);
                            
                            // Renderizar una fila por cada instalación
                            return env.installations.map((installation, instIndex) => {
                              let subtotal = 0;
                              if (installation.installation_service && installation.tipo_instalacion) {
                                // Usar el precio editado (mano_obra_m2) si existe, sino usar el del catálogo
                                const precio = installation.mano_obra_m2
                                  ? installation.mano_obra_m2
                                  : (installation.tipo_instalacion === 'standard' 
                                    ? installation.installation_service.precio_standard 
                                    : installation.installation_service.precio_complejo);
                                if (precio) {
                                  subtotal = precio * (installation.cantidad_manual ?? 1);
                                }
                              } else if (installation.mano_obra_m2) {
                                // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
                              }
                              
                              return (
                                <TableRow key={`installation-${envIndex}-${instIndex}`}>
                                  <TableCell>{env.nombre}</TableCell>
                                  <TableCell>
                                    {installation?.installation_service?.trabajo || installation?.trabajo || 'Instalación personalizada'}
                                  </TableCell>
                                  <TableCell>
                                    {installation?.descripcion_trabajo || installation?.installation_service?.descripcion || '-'}
                                  </TableCell>
                                  <TableCell>
                                    {/* Custom: usar installation.unidad, Catálogo: usar installation_service.unidad */}
                                    {installation?.installation_service_id 
                                      ? (installation?.installation_service?.unidad || '')
                                      : (installation?.unidad || '')}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.installation_service && installation?.tipo_instalacion ? (
                                      installation.mano_obra_m2
                                        ? formatInstallationCurrency(installation.mano_obra_m2, 'ARS')
                                        : (installation.tipo_instalacion === 'standard' 
                                          ? formatInstallationCurrency(installation.installation_service.precio_standard || 0, installation.installation_service.moneda_standard)
                                          : formatInstallationCurrency(installation.installation_service.precio_complejo || 0, installation.installation_service.moneda_compleja))
                                    ) : (
                                      // Para instalaciones custom con unidad global, mostrar "-"
                                      installation?.unidad?.toLowerCase() === 'global' 
                                        ? '-'
                                        : formatCurrency(installation?.mano_obra_m2 || 0, 'instalacion')
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.cantidad_manual || areaTotal.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.installation_service && installation?.tipo_instalacion ? (
                                      installation.tipo_instalacion === 'standard' 
                                        ? formatInstallationCurrency(subtotal, installation.installation_service.moneda_standard)
                                        : formatInstallationCurrency(subtotal, installation.installation_service.moneda_compleja)
                                    ) : (
                                      formatCurrency(subtotal, 'instalacion')
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {!(isReadOnly || isFieldsReadOnly) && (
                                      <IconButton
                                        size="small"
                                        onClick={async () => {
                                          try {
                                            setSaving(true);
                                            // Eliminar del backend si tiene ID
                                            if (installation.id && installation.id > 0) {
                                              await BudgetApiService.deleteEnvironmentInstallation(installation.id);
                                              console.log(`✅ Instalación eliminada del backend`);
                                            }
                                            // Eliminar del estado local
                                            setFormData(prev => {
                                              const newEnvironments = [...prev.environments];
                                              newEnvironments[envIndex].installations = newEnvironments[envIndex].installations.filter((_, idx) => idx !== instIndex);
                                              return { ...prev, environments: newEnvironments };
                                            });
                                          } catch (error) {
                                            console.error('❌ Error eliminando instalación:', error);
                                            setError('Error al eliminar la instalación');
                                          } finally {
                                            setSaving(false);
                                          }
                                        }}
                                        color="error"
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                        })}
                        {/* Fila de total de instalación */}
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell colSpan={6} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            TOTAL INSTALACIÓN:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {(() => {
                              const totales = { ARS: 0, USD: 0, other: 0 };
                              formData.environments.forEach(env => {
                                if (!env.installations || env.installations.length === 0) return;
                                const areaTotal = env.materials.reduce(
                                  (sum, mat) => sum + (mat.metros_cuadrados || 0), 0
                                );
                                
                                env.installations.forEach(installation => {
                                  let subtotal = 0;
                                  let moneda = 'ARS';
                                  if (installation.installation_service && installation.tipo_instalacion) {
                                    // Usar el precio editado (mano_obra_m2) si existe, sino usar el del catálogo
                                    const precio = installation.mano_obra_m2
                                      ? installation.mano_obra_m2
                                      : (installation.tipo_instalacion === 'standard' 
                                        ? installation.installation_service.precio_standard 
                                        : installation.installation_service.precio_complejo);
                                    moneda = installation.tipo_instalacion === 'standard'
                                      ? installation.installation_service.moneda_standard
                                      : installation.installation_service.moneda_compleja;
                                    if (precio) {
                                      subtotal = precio * (installation.cantidad_manual ?? 1);
                                    }
                                  } else if (installation.mano_obra_m2) {
                                    // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                    const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                    subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
                                    moneda = installation.currency || 'ARS';
                                  }
                                  if (moneda === 'ARS') {
                                    totales.ARS += subtotal;
                                  } else if (moneda === 'USD') {
                                    totales.USD += subtotal;
                                  } else {
                                    totales.other += subtotal;
                                  }
                                });
                              });
                              const parts = [];
                              if (totales.ARS > 0) parts.push(formatInstallationCurrency(totales.ARS, 'ARS'));
                              if (totales.USD > 0) parts.push(formatInstallationCurrency(totales.USD, 'USD'));
                              if (totales.other > 0) parts.push(`${totales.other.toFixed(2)}`);
                              return parts.join(' + ') || formatInstallationCurrency(0, 'ARS');
                            })()}                          </TableCell>
                        </TableRow>

                        {/* Fila de descuento de instalación */}
                        <TableRow>
                          <TableCell colSpan={6} align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                              <Typography variant="body2">Descuento (%)</Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={Math.round(formData.descuento_instalacion)}
                                onChange={handleChangeDescuento('descuento_instalacion')}
                                inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                                disabled={(isReadOnly|| isFieldsReadOnly)}
                              />
                            </Box>
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {formData.descuento_instalacion > 0 ? `- ${formatInstallationCurrency(getTotalInstallationsARS() * (formData.descuento_instalacion / 100), 'ARS')}` : '0.00'}
                          </TableCell>
                        </TableRow>
                        {/* Fila de total con descuento */}
                        <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                          <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>
                            TOTAL FINAL CON DESCUENTO:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                            {formatInstallationCurrency(getTotalInstallationsARS() * (1 - formData.descuento_instalacion / 100), 'ARS')}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  No hay servicios de instalación configurados. Agregue instalación a los ambientes según sea necesario.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Fletes */}
        <Card>
          <CardHeader 
            title="Fletes y Envíos"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<LocalShipping color="primary" />}
            subheader={`${formData.freights.length} flete(s) configurado(s)`}
            subheaderTypographyProps={{ variant: 'body1' }}
            sx={{ pb: 1 }}
            action={
              !(isReadOnly || isFieldsReadOnly) && (
                <Box sx={{ mr: 5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setFreightDialog(true)}
                  >
                    Agregar Flete
                  </Button>
                </Box>
              )
            }
          />
          <CardContent sx={{ p: 3 }}>
            {formData.freights.length > 0 ? (
              <Box>
                <Box display="flex" justifyContent="center">
                  <TableContainer component={Paper} sx={{ maxWidth: 'calc(100% - 40px)' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.freights.map((freight, index) => (
                          <TableRow key={`freight-${index}`}>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="medium">
                                {freight.tipo_envio}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {!(isReadOnly || isFieldsReadOnly) ? (
                                <TextField
                                  size="small"
                                  value={freight.descripcion_detalle || ''}
                                  onChange={(e) => {
                                    const newFreights = [...formData.freights];
                                    newFreights[index] = { ...newFreights[index], descripcion_detalle: e.target.value };
                                    setFormData(prev => ({ ...prev, freights: newFreights }));
                                  }}
                                  placeholder="Detalle del flete"
                                  inputProps={{ maxLength: 150 }}
                                  sx={{ minWidth: 200 }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {freight.descripcion_detalle || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {formatCurrency(freight.monto, freight.currency === 'ARS' ? 'AR' : freight.currency)}
                                {freight.tipo === 'porcentaje' && '%'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {!(isReadOnly || isFieldsReadOnly) && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteFreight(index)}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      
                      {/* Fila de total de fletes */}
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        <TableCell colSpan={2} align="left" sx={{ color: 'white', fontWeight: 'bold', pl: 2 }}>
                          <Typography variant="body1" fontWeight="bold" sx={{ color: 'white', textAlign: 'left' }}>
                            Total Fletes:
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          <Typography variant="body1" fontWeight="bold" sx={{ color: 'white', textAlign: 'right' }}>
                            {formatCurrency(getTotalFreights(), 'AR')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: 'white' }}></TableCell>
                      </TableRow>

                      {/* Fila de descuento de fletes */}
                      <TableRow>
                        <TableCell colSpan={2} align="right">
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                            <Typography variant="body2">Descuento (%)</Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={Math.round(formData.descuento_fletes)}
                              onChange={handleChangeDescuento('descuento_fletes')}
                              inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                              disabled={(isReadOnly || isFieldsReadOnly)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {formData.descuento_fletes > 0 ? `- ${formatCurrency(getTotalFreights() * (formData.descuento_fletes / 100), 'ARS')}` : '0.00'}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      {/* Fila de total con descuento */}
                      <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                        <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>
                          TOTAL FINAL CON DESCUENTO:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                          {formatCurrency(getTotalFreights() * (1 - formData.descuento_fletes / 100), 'ARS')}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  No hay fletes configurados
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Condiciones Adicionales */}
        <Card>
          <CardHeader 
            title="Condiciones Adicionales"
            titleTypographyProps={{ variant: 'h6' }}
            avatar={<Assignment color="primary" />}
          />
          <CardContent>
            <TextField
              fullWidth
              label="Condiciones Adicionales"
              multiline
              rows={8}
              value={formData.currentVersion?.condiciones_adicionales || ''}
              onChange={(e) => {
                if (isReadOnly || isFieldsReadOnly) return;
                setFormData(prev => ({
                  ...prev,
                  currentVersion: {
                    ...prev.currentVersion!,
                    condiciones_adicionales: e.target.value
                  }
                }));
              }}
              placeholder="Observaciones adicionales"
              sx={{ mb: 2 }}
              InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
            />
            
            <Box display="flex" justifyContent="center" sx={{ mx: 3 }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowCondiciones(!showCondiciones)}
                startIcon={showCondiciones ? <ExpandMore /> : <Receipt />}
              >
                {showCondiciones ? 'Ocultar Condiciones Obligatorias de Contratación' : 'Ver Condiciones Obligatorias de Contratación'}
              </Button>
            </Box>
            
            {showCondiciones && currentVersion?.condiciones_contratacion && (
              <Box mt={2} sx={{ mx: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Condiciones Obligatorias de Contratación:</Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                    {currentVersion.condiciones_contratacion}
                  </Typography>
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Resumen de Totales por Sección */}
        <Card>
          <CardHeader 
            title="Resumen Financiero"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Receipt color="primary" />}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" flexWrap="wrap" gap={4} sx={{ mx: 3 }}>
              <Box flex="1 1 250px">
                <Box textAlign="center" p={3} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body1" color="text.secondary" gutterBottom fontWeight="medium">
                    Total Materiales
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {formatCurrency(getTotalMaterialsWithDiscounts(), 'USD')}
                  </Typography>
                  {getTotalMaterials() !== getTotalMaterialsWithDiscounts() && (
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Sin descuentos: {formatCurrency(getTotalMaterials(), 'USD')}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box flex="1 1 250px">
                <Box textAlign="center" p={3} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body1" color="text.secondary" gutterBottom fontWeight="medium">
                    Total Instalación
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {formData.descuento_instalacion > 0 ? formatInstallationCurrency(getTotalInstallationsARS() * (1 - formData.descuento_instalacion / 100), 'ARS') : getTotalInstallations()}
                  </Typography>
                  {formData.descuento_instalacion > 0 && (
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Sin descuento: {getTotalInstallations()}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box flex="1 1 250px">
                <Box textAlign="center" p={3} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body1" color="text.secondary" gutterBottom fontWeight="medium">
                    Total Fletes
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {formData.descuento_fletes > 0 ? formatCurrency(getTotalFreights() * (1 - formData.descuento_fletes / 100), 'ARS') : formatCurrency(getTotalFreights(), 'ARS')}
                  </Typography>
                  {formData.descuento_fletes > 0 && (
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Sin descuento: {formatCurrency(getTotalFreights(), 'ARS')}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Card de Historial de Versiones */}
        <Card>
          <CardHeader 
            title="Historial de Versiones"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<AccountBox color="primary" />}
            sx={{ pb: 1 }}
            action={
              <Box sx={{ mr: 5 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<Add />}
                  onClick={() => setNewVersionDialog(true)}
                  sx={{ fontSize: '1.1rem' }}
                >
                  Nueva Versión
                </Button>
              </Box>
            }
          />
          <CardContent sx={{ p: 3 }}>
            {versions.length > 0 ? (
              <Box display="flex" justifyContent="center">
                <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Versión</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Título</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Vendedor</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Creación</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map((version) => (
                      <TableRow key={version.id}>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                            <Typography variant="body2" fontWeight="medium">
                              {version.numero_version || `v${version.version_number || version.id}`}
                            </Typography>
                            {version.is_current && <CheckCircle color="success" fontSize="medium" />}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {version.titulo}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {version.vendedor || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {version.fecha_creacion ? new Date(version.fecha_creacion).toLocaleDateString() : 
                             (version.created_at ? new Date(version.created_at).toLocaleDateString() : '-')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={version.is_current ? 'Activa' : 'Inactiva'}
                            color={version.is_current ? 'success' : 'default'}
                            variant={version.is_current ? 'filled' : 'outlined'}
                            icon={version.is_current ? <CheckCircle /> : undefined}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Descargar PDF">
                              <IconButton 
                                size="small"
                                onClick={async () => {
                                  try {
                                    // Guardar cambios primero
                                    await updateAllBudgetData();
                                    // Luego descargar la versión específica
                                    await handleDownloadPdf(version.id);
                                  } catch (error) {
                                    setError('Error al guardar cambios o descargar PDF.');
                                  }
                                }}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>No hay versiones disponibles</Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Diálogos - Reutilizando componentes del BudgetFormNew */}
      
      {/* Dialog para nuevo ambiente */}
      <Dialog
        open={environmentDialog}
        onClose={() => setEnvironmentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuevo Ambiente</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre del Ambiente *"
              value={newEnvironment.nombre}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                nombre: e.target.value
              }))}
              placeholder="Ej: Living, Cocina, Dormitorio"
            />
            
            <TextField
              fullWidth
              label="Descripción"
              value={newEnvironment.descripcion}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                descripcion: e.target.value
              }))}
              placeholder="Descripción detallada del ambiente"
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvironmentDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddEnvironment}
            variant="contained"
            disabled={!newEnvironment.nombre.trim()}
          >
            Agregar Ambiente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para nuevo material - Replicando funcionalidad completa del form */}
      <Dialog
        open={materialDialog}
        onClose={() => {
          setMaterialDialog(false);
          setSelectedEnvironmentIndex(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Agregar Material al Ambiente</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Autocomplete
              options={products}
              getOptionLabel={(option: Product) => `${option.codigo} ${option.sku ? `- ${option.sku}` : ''} ${option.color ? `(${option.color})` : ''}`}
              value={!newMaterial.es_in_situ ? (newMaterial.product || null) : null}
              onChange={(event, newValue) => handleProductSelect(newValue)}
              disabled={newMaterial.es_in_situ}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Producto *"
                  placeholder="Buscar por descripción, código, color o grupo..."
                />
              )}
              renderOption={(props: any, option: Product) => (
                <Box component="li" {...props}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight="bold">
                        {option.descripcion || option.codigo}
                      </Typography>
                      {option.descuento === -1 && (
                        <Chip label="Outlet" size="small" color="warning" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Código: {option.codigo} | Grupo: {option.grupo || 'Sin grupo'} | Color: {option.color || 'Sin color'} | Precio: {formatCurrency(option.precio_unitario || 0)}
                    </Typography>
                    {option.largo && option.ancho && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Dimensiones: {option.largo} × {option.ancho} {option.espesor ? `× ${option.espesor}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              filterOptions={(options: Product[], { inputValue }: { inputValue: string }) => {
                if (!inputValue) return options;
                const lowerInput = inputValue.toLowerCase();
                return options.filter((option: Product) =>
                  (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput)) ||
                  (option.grupo && option.grupo.toLowerCase().includes(lowerInput)) ||
                  option.codigo.toLowerCase().includes(lowerInput) ||
                  (option.color && option.color.toLowerCase().includes(lowerInput)) ||
                  (option.sku && option.sku.toLowerCase().includes(lowerInput))
                );
              }}
            />

            {/* Toggle para producto manual (abajo del selector) */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newMaterial.es_in_situ}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      es_in_situ: e.target.checked,
                      product_id: undefined,
                      product: undefined,
                      descripcion: '',
                      codigo: '',
                      precio_unitario: 0,
                      cantidad_calculada: 0,
                      metros_cuadrados: 1,
                      usar_cantidad_manual: false,
                      con_desperdicio: true,
                      con_estructura: false,
                      observaciones: ''
                    }))}
                  />
                }
                label="Ingresar producto manual (no existente en catálogo)"
              />
              {newMaterial.es_in_situ && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Este producto no se creará en el Catálogo, solo aparecerá en este presupuesto
                </Alert>
              )}
            </Box>

            {!newMaterial.es_in_situ ? (
              // Selección de producto existente - opciones adicionales
              <>
                {newMaterial.product && (
                  <>
                    {/* Opciones de desperdicio y estructura */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Opciones del Producto:
                      </Typography>
                      <Stack spacing={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newMaterial.con_desperdicio || false}
                              onChange={(e) => handleDesperdicioChange(e.target.checked)}
                            />
                          }
                          label="Incluir desperdicio (+10%)"
                        />
                        {newMaterial.product.estructura_m2 && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newMaterial.con_estructura || false}
                                onChange={(e) => setNewMaterial(prev => ({
                                  ...prev,
                                  con_estructura: e.target.checked
                                }))}
                              />
                            }
                            label="Incluir estructura"
                          />
                        )}
                      </Stack>
                    </Box>

                    {/* Campos de m² y cantidad que se completan mutuamente */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Cálculo de Cantidad:
                      </Typography>
                      <Box display="flex" gap={2}>
                        <TextField
                          fullWidth
                          label="Metros Cuadrados"
                          type="number"
                          value={newMaterial.metros_cuadrados || 1}
                          onChange={(e) => {
                            if (!newMaterial.usar_cantidad_manual) {
                              handleMetrosCuadradosChange(parseFloat(e.target.value) || 1);
                            } else {
                              setNewMaterial(prev => ({
                                ...prev,
                                metros_cuadrados: parseFloat(e.target.value) || 1
                              }));
                            }
                          }}
                          placeholder="Área a cubrir en m²"
                          inputProps={{ min: 0.1, step: 0.1 }}
                        />

                        <TextField
                          fullWidth
                          label="Cantidad (unidades)"
                          type="number"
                          value={newMaterial.usar_cantidad_manual ? (newMaterial.cantidad_manual || 0) : (newMaterial.cantidad_calculada || 0)}
                          onChange={(e) => {
                            const cantidad = parseFloat(e.target.value) || 0;
                            setNewMaterial(prev => ({ ...prev, usar_cantidad_manual: true }));
                            handleCantidadManualChange(cantidad);
                          }}
                          placeholder="Cantidad específica"
                          inputProps={{ min: 0, step: 1 }}
                        />
                      </Box>
                    </Box>

                    {newMaterial.cantidad_calculada && newMaterial.cantidad_calculada > 0 && !newMaterial.usar_cantidad_manual && (
                      <Alert severity="info">
                        <Typography variant="body2">
                          Cantidad calculada automáticamente: <strong>{newMaterial.cantidad_calculada} unidades</strong>
                          {newMaterial.con_desperdicio && <span> (incluye 10% desperdicio)</span>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Basado en {newMaterial.metros_cuadrados}m² ÷ ({newMaterial.product.largo}m × {newMaterial.product.ancho_util ?? newMaterial.product.ancho}m{newMaterial.product.ancho_util ? ' útil' : ''} por unidad)
                        </Typography>
                      </Alert>
                    )}

                    {newMaterial.usar_cantidad_manual && newMaterial.cantidad_manual && newMaterial.product && (
                      <Alert severity="success">
                        <Typography variant="body2">
                          Metros cuadrados calculados automáticamente: <strong>{newMaterial.metros_cuadrados}m²</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Basado en {newMaterial.cantidad_manual} unidades × ({newMaterial.product.largo}m × {newMaterial.product.ancho_util ?? newMaterial.product.ancho}m{newMaterial.product.ancho_util ? ' útil' : ''} por unidad)
                          {newMaterial.con_desperdicio && <span> (considerando desperdicio incluido)</span>}
                        </Typography>
                      </Alert>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newMaterial.usar_cantidad_manual || false}
                          onChange={(e) => {
                            const useManual = e.target.checked;
                            setNewMaterial(prev => ({
                              ...prev,
                              usar_cantidad_manual: useManual,
                              unidades: useManual ? (prev.cantidad_manual || 0) : (prev.cantidad_calculada || 0)
                            }));
                          }}
                        />
                      }
                      label="Usar cantidad manual (deshabilita cálculo automático)"
                    />

                    {/* Campo de observaciones */}
                    <TextField
                      fullWidth
                      label="Observaciones"
                      value={newMaterial.observaciones || ''}
                      onChange={(e) => setNewMaterial(prev => ({
                        ...prev,
                        observaciones: e.target.value.slice(0, 100)
                      }))}
                      placeholder="Observaciones sobre este material"
                      multiline
                      rows={2}
                      inputProps={{ maxLength: 100 }}
                      helperText={`${(newMaterial.observaciones || '').length}/100 caracteres`}
                    />

                    {/* Vista previa de estructura si está marcada */}
                    {newMaterial.con_estructura && newMaterial.product.estructura_m2 && (
                      <Alert severity="info">
                        <Typography variant="subtitle2" gutterBottom>
                          ⚠️ Estructura incluida
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {newMaterial.product.grupo === 'PERFIL'
                            ? `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por unidad (total unidades: ${newMaterial.unidades || 1}).`
                            : `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por metro cuadrado (total m²: ${newMaterial.metros_cuadrados || 1}).`}
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                          {newMaterial.product.estructura_m2.split(/[;\n]+/).map((entry, index) => {
                            const trimmedEntry = entry.trim();
                            if (!trimmedEntry) return null;
                            try {
                              const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
                              const codigoTrimmed = codigo.trim();
                              const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
                              const structureProduct = products.find((p: Product) => p.codigo === codigoTrimmed);
                              let cantidadBase, cantidadTotal, cantidadFinal, detalleCalculo;
                              if (newMaterial.product && newMaterial.product.grupo === 'PERFIL') {
                                cantidadBase = newMaterial.unidades || 1;
                                cantidadTotal = cantidadPorM2 * cantidadBase;
                                cantidadFinal = Math.ceil(cantidadTotal);
                                detalleCalculo = `(${cantidadPorM2}/unidad × ${cantidadBase} unidades)`;
                              } else {
                                cantidadBase = newMaterial.metros_cuadrados || 1;
                                cantidadTotal = cantidadPorM2 * cantidadBase;
                                cantidadFinal = Math.ceil(cantidadTotal);
                                detalleCalculo = `(${cantidadPorM2}/m² × ${cantidadBase}m²)`;
                              }
                              return (
                                <Typography 
                                  key={index} 
                                  component="li" 
                                  variant="caption" 
                                  color="text.secondary"
                                >
                                  <strong>{codigoTrimmed}</strong>
                                  {structureProduct && ` (${structureProduct.sku || 'Sin SKU'})`}: 
                                  {' '}{cantidadFinal} unidades
                                  <span style={{ color: '#666' }}>
                                    {' '}{detalleCalculo}
                                  </span>
                                  <span style={{ color: '#888', fontSize: '0.8em' }}>
                                    {' '}(sin desperdicio)
                                  </span>
                                </Typography>
                              );
                            } catch {
                              return null;
                            }
                          })}
                        </Box>
                      </Alert>
                    )}
                  </>
                )}
              </>
            ) : (
              // Creación de material in situ
              <>
                <TextField
                  fullWidth
                  label="Código *"
                  value={newMaterial.codigo}
                  onChange={(e) => setNewMaterial(prev => ({
                    ...prev,
                    codigo: e.target.value
                  }))}
                  placeholder="Código único del material"
                  required
                />

                <TextField
                  fullWidth
                  label="Descripción *"
                  value={newMaterial.descripcion}
                  onChange={(e) => setNewMaterial(prev => ({
                    ...prev,
                    descripcion: e.target.value
                  }))}
                  placeholder="Descripción detallada del material"
                  required
                />
                
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Unidades"
                    type="number"
                    value={newMaterial.unidades}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      unidades: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Cantidad"
                  />

                  <TextField
                    fullWidth
                    label="Precio Unitario"
                    type="number"
                    value={newMaterial.precio_unitario}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      precio_unitario: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Precio por unidad"
                  />

                  <FormControl fullWidth>
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      value={newMaterial.currency}
                      onChange={(e) => setNewMaterial(prev => ({
                        ...prev,
                        currency: e.target.value as 'USD' | 'ARS'
                      }))}
                      label="Moneda"
                    >
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="ARS">ARS</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {/* Resumen del cálculo */}
            {(newMaterial.unidades > 0 && newMaterial.precio_unitario > 0) && (
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Resumen del Material
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    {newMaterial.unidades} unidades × {formatCurrency(newMaterial.precio_unitario)}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(newMaterial.unidades * newMaterial.precio_unitario)}
                  </Typography>
                </Box>
                {newMaterial.product && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Producto: {newMaterial.product.codigo} {newMaterial.product.sku ? `(${newMaterial.product.sku})` : ''}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMaterialDialog(false);
            setSelectedEnvironmentIndex(null);
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddMaterial}
            variant="contained"
            disabled={
              newMaterial.es_in_situ 
                ? (!newMaterial.codigo.trim() || !newMaterial.descripcion.trim())
                : !newMaterial.product_id
            }
          >
            Agregar Material
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resto de diálogos - Simplificados */}
      <Dialog open={freightDialog} onClose={() => setFreightDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Flete</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Tipo Flete</InputLabel>
              <Select
                value={newFreight.tipo_envio}
                onChange={(e) => setNewFreight(prev => ({ ...prev, tipo_envio: e.target.value }))}
                label="Tipo Flete"
              >
                <MenuItem value="Flete y acarreo PB">Flete y acarreo PB</MenuItem>
                <MenuItem value="Flete y acarreo especial">Flete y acarreo especial</MenuItem>
                <MenuItem value="Flete sin acarreo">Flete sin acarreo</MenuItem>
              </Select>
            </FormControl>

            {/* Campo de descripción detallada opcional */}
            <TextField
              fullWidth
              label="Descripción detallada (opcional)"
              multiline
              rows={2}
              value={newFreight.descripcion_detalle || ''}
              onChange={(e) => setNewFreight(prev => ({
                ...prev,
                descripcion_detalle: e.target.value
              }))}
              placeholder="Detalles del flete: ubicación, condiciones especiales, etc."
              inputProps={{ maxLength: 150 }}
              helperText={`${(newFreight.descripcion_detalle || '').length}/150 caracteres`}
            />

            <TextField
              fullWidth
              label="Monto"
              type="number"
              value={newFreight.monto}
              onChange={(e) => setNewFreight(prev => ({ ...prev, monto: parseFloat(e.target.value) || 0 }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreightDialog(false)}>Cancelar</Button>
          <Button onClick={handleAddFreight} variant="contained" disabled={!newFreight.tipo_envio.trim()}>
            Agregar Flete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={installationDialog}
        onClose={() => {
          setInstallationDialog(false);
          setSelectedEnvironmentIndex(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Instalación del Ambiente</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Selector de ambiente */}
            <FormControl fullWidth required>
              <InputLabel>Seleccionar Ambiente</InputLabel>
              <Select
                value={selectedEnvironmentIndex !== null ? selectedEnvironmentIndex : ''}
                onChange={(e) => setSelectedEnvironmentIndex(Number(e.target.value))}
                label="Seleccionar Ambiente"
              >
                <MenuItem value={-1}>
                  <em>Instalaciones Generales</em>
                </MenuItem>
                {formData.environments
                  .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                  .map((env, index) => {
                    // Find the actual index in formData.environments
                    const actualIndex = formData.environments.findIndex(e => e === env);
                    return (
                      <MenuItem 
                        key={actualIndex} 
                        value={actualIndex}
                      >
                        {env.nombre}
                      </MenuItem>
                    );
                  })}
              </Select>
              {selectedEnvironmentIndex !== null && selectedEnvironmentIndex >= 0 && formData.environments[selectedEnvironmentIndex] && (
                <Typography variant="caption" color="text.secondary">
                  Área total del ambiente: {formData.environments[selectedEnvironmentIndex].materials.reduce(
                    (total, mat) => total + (mat.metros_cuadrados || 0), 0
                  ).toFixed(2)} m²
                </Typography>
              )}
            </FormControl>

            {/* Toggle para elegir modo de instalación */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={installationMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setInstallationMode(newMode);
                    // Limpiar campos al cambiar de modo
                    setNewInstallation({
                      cantidad_manual: 1,
                      currency: 'ARS'
                    });
                  }
                }}
                size="small"
              >
                <ToggleButton value="catalog">
                  Desde Catálogo
                </ToggleButton>
                <ToggleButton value="custom">
                  Personalizada
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* MODO CATÁLOGO */}
            {installationMode === 'catalog' && (
              <>
                {/* Buscador de servicios de instalación */}
                <Autocomplete
                  fullWidth
                  options={installationServices}
                  getOptionLabel={(option) => `${option.trabajo} (${option.unidad})`}
                  value={newInstallation.installation_service || null}
                  onChange={(event, newValue) => handleInstallationServiceSelect(newValue)}
                  loading={loadingInstallationServices}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Servicio de Instalación *"
                      placeholder="Buscar por nombre de trabajo..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingInstallationServices ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.trabajo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Unidad: {option.unidad}
                          {option.precio_standard && ` | Estándar: ${formatInstallationCurrency(option.precio_standard, option.moneda_standard)}`}
                          {option.precio_complejo && ` | Complejo: ${formatInstallationCurrency(option.precio_complejo, option.moneda_compleja)}`}
                        </Typography>
                        {option.descripcion && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {option.descripcion}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const lowerInput = inputValue.toLowerCase();
                    return options.filter((option) =>
                      option.trabajo.toLowerCase().includes(lowerInput) ||
                      (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput)) ||
                      option.unidad.toLowerCase().includes(lowerInput)
                    );
                  }}
                />

                {/* Selección de tipo de instalación si hay servicio seleccionado */}
                {newInstallation.installation_service && (
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Instalación</InputLabel>
                    <Select
                      value={newInstallation.tipo_instalacion || ''}
                      onChange={(e) => setNewInstallation(prev => ({
                        ...prev,
                        tipo_instalacion: e.target.value as any
                      }))}
                      label="Tipo de Instalación"
                    >
                      {newInstallation.installation_service.precio_standard && (
                        <MenuItem value="standard">
                          Estándar - {formatInstallationCurrency(newInstallation.installation_service.precio_standard, newInstallation.installation_service.moneda_standard)}
                        </MenuItem>
                      )}
                      {newInstallation.installation_service.precio_complejo && (
                        <MenuItem value="compleja">
                          Compleja - {formatInstallationCurrency(newInstallation.installation_service.precio_complejo, newInstallation.installation_service.moneda_compleja)}
                        </MenuItem>
                      )}
                      <MenuItem value="custom">Precio personalizado</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Campo de cantidad manual - CATÁLOGO */}
                <TextField
                  fullWidth
                  label="Cantidad"
                  type="number"
                  value={newInstallation.cantidad_manual}
                  onChange={(e) => setNewInstallation(prev => ({
                    ...prev,
                    cantidad_manual: parseFloat(e.target.value) || 1
                  }))}
                  placeholder="Cantidad a instalar"
                  inputProps={{ min: 0.1, step: 0.1 }}
                  InputLabelProps={{ shrink: true }}
                  helperText={newInstallation.installation_service 
                    ? `Unidad: ${newInstallation.installation_service.unidad}`
                    : "Cantidad de unidades a instalar"
                  }
                  required
                />

                {/* Campo de precio - precargado con valor calculado si existe */}
                {newInstallation.tipo_instalacion && newInstallation.installation_service && (
                  <TextField
                    fullWidth
                    label="Precio por unidad"
                    type="number"
                    value={newInstallation.mano_obra_m2 || calculateInstallationPrice()}
                    onChange={(e) => setNewInstallation(prev => ({
                      ...prev,
                      mano_obra_m2: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Precio por unidad"
                    InputProps={{
                      endAdornment: <span style={{ marginLeft: '8px', color: '#666' }}>ARS</span>
                    }}
                    helperText={newInstallation.tipo_instalacion === 'custom' ? 'Ingresa el precio personalizado' : 'Campo precargado con precio del catálogo (editable)'}
                  />
                )}
              </>
            )}

            {/* MODO PERSONALIZADO */}
            {installationMode === 'custom' && (
              <>
                {/* Nombre del trabajo */}
                <TextField
                  fullWidth
                  label="Nombre del trabajo *"
                  value={newInstallation.trabajo || ''}
                  onChange={(e) => setNewInstallation(prev => ({
                    ...prev,
                    trabajo: e.target.value,
                    tipo_instalacion: 'custom'
                  }))}
                  placeholder="Ej: Instalación de vidrio templado"
                  required
                />

                {/* Unidad y Cantidad en la misma fila */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Unidad</InputLabel>
                    <Select
                      value={newInstallation.unidad || ''}
                      onChange={(e) => setNewInstallation(prev => ({
                        ...prev,
                        unidad: e.target.value
                      }))}
                      label="Unidad"
                    >
                      <MenuItem value="m2">m2</MenuItem>
                      <MenuItem value="ml">ml (metro lineal)</MenuItem>
                      <MenuItem value="unit">Unidad</MenuItem>
                      <MenuItem value="global">Global</MenuItem>
                      <MenuItem value="hora">Hora</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Cantidad"
                    type="number"
                    value={newInstallation.cantidad_manual}
                    onChange={(e) => setNewInstallation(prev => ({
                      ...prev,
                      cantidad_manual: parseFloat(e.target.value) || 1
                    }))}
                    placeholder="Cantidad"
                    inputProps={{ min: 0.1, step: 0.1 }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Box>

                {/* Precio y Moneda en la misma fila */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Precio por unidad *"
                    type="number"
                    value={newInstallation.mano_obra_m2 || ''}
                    onChange={(e) => setNewInstallation(prev => ({
                      ...prev,
                      mano_obra_m2: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Precio"
                    inputProps={{ min: 0, step: 0.01 }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                  <FormControl sx={{ minWidth: 100 }}>
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      value={newInstallation.currency}
                      onChange={(e) => setNewInstallation(prev => ({
                        ...prev,
                        currency: e.target.value as 'USD' | 'ARS'
                      }))}
                      label="Moneda"
                    >
                      <MenuItem value="ARS">ARS</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {/* Campo de descripción editable - COMÚN A AMBOS MODOS */}
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={2}
              value={newInstallation.descripcion_trabajo || ''}
              onChange={(e) => setNewInstallation(prev => ({
                ...prev,
                descripcion_trabajo: e.target.value
              }))}
              placeholder="Descripción del trabajo de instalación"
              helperText="Descripción del servicio (opcional)"
            />

            {/* Resumen de costo */}
            {selectedEnvironmentIndex !== null && (
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>
                  Resumen de Instalación
                </Typography>
                <Typography variant="body2">
                  Servicio: {installationMode === 'catalog' 
                    ? (newInstallation.installation_service?.trabajo || 'No seleccionado')
                    : (newInstallation.trabajo || 'Sin nombre')}
                </Typography>
                <Typography variant="body2">
                  Cantidad: {newInstallation.cantidad_manual} {
                    installationMode === 'catalog' 
                      ? (newInstallation.installation_service?.unidad || 'unidades')
                      : (newInstallation.unidad || '')
                  }
                </Typography>
                <Typography variant="body2">
                  Precio unitario: {formatCurrency(
                    installationMode === 'catalog' ? calculateInstallationPrice() : (newInstallation.mano_obra_m2 || 0), 
                    'instalacion'
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Total: {formatCurrency(
                    installationMode === 'catalog' 
                      ? calculateInstallationPrice() * (newInstallation.cantidad_manual ?? 1)
                      : (newInstallation.unidad?.toLowerCase() === 'global' 
                          ? (newInstallation.mano_obra_m2 || 0) 
                          : (newInstallation.mano_obra_m2 || 0) * (newInstallation.cantidad_manual ?? 1)), 
                    'instalacion'
                  )}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setInstallationDialog(false);
            setSelectedEnvironmentIndex(null);
            setInstallationMode('catalog');
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddInstallation}
            variant="contained"
            disabled={
              selectedEnvironmentIndex === null || 
              (newInstallation.cantidad_manual ?? 0) <= 0 ||
              (installationMode === 'catalog' && !newInstallation.installation_service_id) ||
              (installationMode === 'custom' && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2))
            }
          >
            Agregar Instalación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newVersionDialog} onClose={() => setNewVersionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear Nueva Versión</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="select-version-label">Versión base cerrada *</InputLabel>
              <Select
                labelId="select-version-label"
                value={newVersionData.numero_version || ''}
                label="Versión base cerrada *"
                onChange={e => setNewVersionData(prev => ({ ...prev, numero_version: e.target.value }))}
              >
                {formData.versions.filter(v => v.estado === 'closed').length === 0 && (
                  <MenuItem value="" disabled>No hay versiones cerradas</MenuItem>
                )}
                {formData.versions.filter(v => v.estado === 'closed').map(v => (
                  <MenuItem value={v.version_number} key={v.id}>
                    {`Versión ${v.version_number} - ${v.titulo}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVersionDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateVersion} variant="contained" disabled={saving || !newVersionData.numero_version}>
            {saving ? <CircularProgress size={20} /> : 'Crear Versión'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar Estado del Presupuesto</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Nuevo Estado</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Nuevo Estado"
            >
              {formData.currentVersion?.estado === 'draft' && [
                <MenuItem value="closed" key="closed">Cerrado</MenuItem>,
                <MenuItem value="sold" key="sold">Vendido</MenuItem>,
                <MenuItem value="lost" key="lost">Perdido</MenuItem>
              ]}
              {formData.currentVersion?.estado === 'closed' && [
                <MenuItem value="sold" key="sold">Vendido</MenuItem>,
                <MenuItem value="lost" key="lost">Perdido</MenuItem>
              ]}
              {formData.currentVersion?.estado === 'lost' && (
                <MenuItem value="sold" key="sold">Vendido</MenuItem>
              )}
              {formData.currentVersion?.estado === 'sold' && user?.role === 'admin' && (
                <MenuItem value="closed" key="closed">Cerrado</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancelar</Button>
          <Button onClick={handleStatusChange} variant="contained" disabled={saving || !selectedStatus}>
            {saving ? <CircularProgress size={20} /> : 'Cambiar Estado'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Eliminar Presupuesto</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Esta acción no se puede deshacer</Alert>
          <Typography>¿Estás seguro de que deseas eliminar el presupuesto <strong>#{formData.code}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleDeleteBudget} variant="contained" color="error" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Eliminar Presupuesto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alerta de stock */}
      <Snackbar
        open={showAlertStock && !!alertStock}
        autoHideDuration={null}
        onClose={() => {
          setShowAlertStock(false);
          setAlertStock(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: '400px',
            justifyContent: 'center',
            alignItems: 'center',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'fixed',
            zIndex: 1500,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            boxShadow: 8,
          }
        }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setShowAlertStock(false);
                setAlertStock(null);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: '100%',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            bgcolor: '#d32f2f', // Rojo fuerte
            color: '#fff',
            border: '2px solid #b71c1c',
            boxShadow: 8,
          }}
        >
          {alertStock}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetDetailNew;
